import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import OutreachPanel from "@/components/admin/OutreachPanel";
import {
  authHeaders,
  getCurrentUser,
  handleUnauthorized,
} from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  restaurant_name: "",
  instagram_handle: "",
  city: "",
  cuisine: "",
  hero_image_url: "",
  website_url: "",
  menu_url: "",
  address: "",
  phone: "",
  hours: "",
  offer: "",
  custom_offer: "",
  description: "",
  cta_text: "",
  custom_cta_text: "",
  cta_link: "",
  after_claim_button_text: "",
  after_claim_button_link: "",
  custom_after_claim_button_text: "",
  offer_terms: "Valid this week only · One per table · Not combinable with other offers",
  campaign_type: "offer",
  featured_item_name: "",
  featured_item_description: "",
  featured_action_text: "View Menu",
  featured_action_link: "",
  event_name: "",
  event_date: "",
  event_time: "",
  event_description: "",
  event_action_text: "View Event Details",
  event_action_link: "",
  custom_event_action_text: "",
  brand_color: "#00E5FF",
  recommended_brand_color: "#00E5FF",
  theme: "default",
  owner_id: "",
  owner_email: "",
  assigned_to: "",
  notes: "",
  source_lead_id: "",
};

const THEME_OPTIONS = [
  ["default", "Default / TapToTable — cyan"],
  ["pizza", "Pizza Shop — tomato red"],
  ["burger", "Burger Joint — mustard yellow"],
  ["tacos", "Taco / Mexican — fresh green"],
  ["cafe", "Cafe / Breakfast — espresso brown"],
  ["italian", "Italian — deep red"],
  ["fine_dining", "Fine Dining / Mediterranean — gold"],
  ["bbq", "BBQ — burnt orange"],
  ["seafood", "Seafood — ocean blue"],
  ["cajun", "Cajun / Indian — spicy red-orange"],
  ["chicken", "Chicken — golden amber"],
  ["chinese", "Chinese — crimson red"],
  ["sushi", "Sushi / Japanese / Thai — indigo"],
  ["icecream", "Ice Cream / Dessert — pink"],
  ["bakery", "Bakery — warm tan"],
  ["hotdog", "Hot Dogs — ketchup red"],
];

const THEME_RECOMMENDED_COLORS = {
  default: "#00E5FF",
  pizza: "#E63946",
  burger: "#D4AF37",
  tacos: "#2E7D32",
  cafe: "#8B5E3C",
  italian: "#E63946",
  fine_dining: "#D4AF37",
  bbq: "#F97316",
  seafood: "#0288D1",
  cajun: "#F97316",
  chicken: "#D4AF37",
  chinese: "#B91C1C",
  sushi: "#6D3CCB",
  icecream: "#EC4899",
  bakery: "#8B5E3C",
  hotdog: "#E63946",
};

const ADMIN_COLOR_PRESETS = [
  ["Cyan", "#00E5FF"],
  ["Red", "#E63946"],
  ["Orange", "#F97316"],
  ["Gold", "#D4AF37"],
  ["Lime", "#84CC16"],
  ["Green", "#2E7D32"],
  ["Teal", "#0F9D8A"],
  ["Blue", "#0288D1"],
  ["Navy", "#1E3A8A"],
  ["Purple", "#6D3CCB"],
  ["Violet", "#8B5CF6"],
  ["Pink", "#EC4899"],
  ["Rose", "#E11D48"],
  ["Brown", "#8B5E3C"],
  ["Burgundy", "#7F1D1D"],
  ["Slate", "#64748B"],
];

function themeRecommendedColor(theme = "default") {
  return THEME_RECOMMENDED_COLORS[theme] || THEME_RECOMMENDED_COLORS.default;
}

function adminThemeColor(form = {}) {
  const custom = String(form.brand_color || "").trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(custom)) return custom.toUpperCase();
  return themeRecommendedColor(form.theme || "default");
}

function adminColorName(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  const match = ADMIN_COLOR_PRESETS.find(
    ([, color]) => color.toLowerCase() === normalized
  );
  return match ? match[0] : "Custom";
}

const OFFER_OPTIONS_BY_THEME = {
  pizza: [
    "Free Garlic Knots with Any Pizza",
    "10% Off Any Large Pizza",
    "Free Drink with Two Slices",
  ],
  bbq: [
    "Free Side with Any BBQ Platter",
    "10% Off Family Packs",
    "Free Drink with Any BBQ Sandwich",
  ],
  sushi: [
    "Free Appetizer with Any Two Rolls",
    "10% Off Orders of $40 or More",
    "Free Drink with Any Lunch Roll Combo",
  ],
  seafood: [
    "Free Appetizer with Any Two Entrées",
    "10% Off Dinner for Two",
    "Free Drink with Any Lunch Entrée",
  ],
  cafe: [
    "Free Pastry with Any Large Coffee",
    "Free Cookie with Any Specialty Drink",
    "Free Coffee with Any Breakfast Entrée",
  ],
  bakery: [
    "Free Coffee with Any Pastry",
    "Free Cookie with Purchase",
    "Buy One Pastry, Get One 50% Off",
  ],
  tacos: [
    "Free Chips and Salsa with Any Two Entrées",
    "10% Off Orders of $40 or More",
    "Free Drink with Any Taco Combo",
  ],
  mexican: [
    "Free Chips and Salsa with Any Two Entrées",
    "10% Off Orders of $40 or More",
    "Free Drink with Any Taco Combo",
  ],
  bar: [
    "Free Appetizer with Two Entrées",
    "10% Off Food During the Game",
    "Free Appetizer with Any Table Order",
  ],
  default: [
    "Free Appetizer with Purchase",
    "10% Off Your First Visit",
    "Free Dessert with Purchase",
  ],
};

const BUTTON_OPTIONS_BY_THEME = {
  pizza: ["Claim Pizza Deal", "Claim My Pizza Offer", "Get This Pizza Deal"],
  bbq: ["Claim BBQ Deal", "Claim My BBQ Offer", "Get This BBQ Deal"],
  sushi: ["Claim Sushi Deal", "Claim My Sushi Offer", "Get This Sushi Deal"],
  seafood: ["Claim Seafood Deal", "Claim My Seafood Offer", "Get This Seafood Deal"],
  cafe: ["Claim Coffee Deal", "Claim My Cafe Offer", "Get This Deal"],
  bakery: ["Claim Bakery Deal", "Claim My Bakery Offer", "Get This Deal"],
  tacos: ["Claim Taco Deal", "Claim My Taco Offer", "Get This Deal"],
  mexican: ["Claim Taco Deal", "Claim My Mexican Offer", "Get This Deal"],
  bar: ["Claim Happy Hour Deal", "Claim My Offer", "Get This Deal"],
  default: ["Claim My Offer", "Get This Deal", "Claim Special"],
};

function resolvedThemeFor(form = {}) {
  const selectedTheme = form.theme || "default";
  const detectedTheme = detectTheme(form.cuisine || "");

  if (selectedTheme === "default" && detectedTheme !== "default") {
    return detectedTheme;
  }

  return selectedTheme;
}

function offerChoicesFor(form = {}) {
  const theme = resolvedThemeFor(form);
  return OFFER_OPTIONS_BY_THEME[theme] || OFFER_OPTIONS_BY_THEME.default;
}

function buttonChoicesFor(form = {}) {
  const theme = resolvedThemeFor(form);
  return BUTTON_OPTIONS_BY_THEME[theme] || BUTTON_OPTIONS_BY_THEME.default;
  
}
const PROMOTION_MESSAGE_OPTIONS_BY_THEME = {
  cafe: [
    "Start your day with something worth stopping in for.",
    "Your next coffee break just got better.",
    "Stop in and enjoy a fresh coffee and something delicious.",
  ],

  bakery: [
    "Treat yourself to something freshly baked today.",
    "Fresh from the oven and ready for your next visit.",
    "Stop in for something sweet, fresh, and delicious.",
  ],

  pizza: [
    "Pizza night just got even better.",
    "Bring your appetite and enjoy a fresh pizza today.",
    "Make your next pizza night one to remember.",
  ],

  tacos: [
    "Make today a taco day.",
    "Fresh flavors and a great reason to stop in.",
    "Your next taco craving has an answer.",
  ],

  bbq: [
    "Come hungry and enjoy some serious BBQ.",
    "Slow-cooked flavor is waiting for you.",
    "Make your next meal a BBQ stop.",
  ],

  sushi: [
    "Fresh rolls and a great reason to stop in.",
    "Make your next meal a sushi night.",
    "Fresh flavors are waiting for you.",
  ],

  seafood: [
    "Fresh seafood and a great reason to dine out.",
    "Make tonight a seafood night.",
    "Stop in and enjoy something fresh from the sea.",
  ],

  default: [
    "Give customers a great reason to stop in today.",
    "Make your next visit even better with this special offer.",
    "Stop in and enjoy something special.",
  ],
};

function promotionMessageChoicesFor(form = {}) {
  const theme = resolvedThemeFor(form);

  return (
    PROMOTION_MESSAGE_OPTIONS_BY_THEME[theme] ||
    PROMOTION_MESSAGE_OPTIONS_BY_THEME.default
  );
}

const AFTER_CLAIM_OPTIONS = [
  "View Menu",
  "Get Directions",
  "Visit Website",
  "Order Online",
  "Reserve Table",
  "Join Rewards",
  "Custom...",
];

const OFFER_TERMS_OPTIONS = [
  "Valid through Sunday · One per customer · Not combinable with other offers",
  "Valid this week only · One per table · Dine-in only",
  "Valid through the end of the month · One per customer",
  "Mention this offer when ordering · Cannot be combined with other discounts",
  "Dine-in only · One offer per table",
  "Takeout only · Must mention offer when ordering",
  "Valid during regular business hours · While supplies last",
  "Not valid with third-party delivery apps",
  "Custom...",
];

const ADMIN_QUICK_NOTE_OPTIONS = [
  "Needs owner follow-up",
  "Waiting for restaurant photos",
  "Waiting for menu link",
  "Waiting for website or rewards link",
  "Needs promotion details",
  "Needs event details",
  "Customer requested changes",
  "Ready to send review link",
  "Review sent — waiting for response",
  "Ready for activation",
  "Payment pending",
  "Hold — do not contact yet",
];


const EVENT_HOURS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1)
);
const EVENT_MINUTES = ["00", "15", "30", "45"];
const EVENT_MERIDIEMS = ["AM", "PM"];

function parseEventTime(value = "") {
  const fallback = {
    startHour: "6",
    startMinute: "00",
    startMeridiem: "PM",
    endHour: "7",
    endMinute: "00",
    endMeridiem: "PM",
  };

  const match = String(value).match(
    /(\d{1,2}):(\d{2})\s*(AM|PM)\s*[–—-]\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i
  );

  if (!match) return fallback;

  return {
    startHour: String(Number(match[1]) || 6),
    startMinute: match[2],
    startMeridiem: match[3].toUpperCase(),
    endHour: String(Number(match[4]) || 7),
    endMinute: match[5],
    endMeridiem: match[6].toUpperCase(),
  };
}

function updateEventTime(value = "", key, nextValue) {
  const parts = {
    ...parseEventTime(value),
    [key]: nextValue,
  };

  return `${parts.startHour}:${parts.startMinute} ${parts.startMeridiem} – ${parts.endHour}:${parts.endMinute} ${parts.endMeridiem}`;
}

const EVENT_ACTION_OPTIONS = [
  "View Event Details",
  "RSVP",
  "Reserve Table",
  "Get Tickets",
  "Join Guest List",
  "Save My Spot",
  "See Event Details",
  "Plan My Visit",
  "Custom...",
];

const EVENT_DESTINATION_OPTIONS = [
  ["website", "Restaurant Website"],
  ["menu", "Menu"],
  ["reservation", "Reservation Page"],
  ["tickets_rsvp", "Ticket / RSVP / Guest List Page"],
  ["event_details", "Event Details / Facebook Event"],
  ["instagram", "Instagram Profile"],
  ["custom", "Custom Link"],
  ["none", "No Link Yet"],
];

function normalizeExternalUrl(value = "") {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  if (/^(https?:|mailto:|tel:|sms:)/i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

function urlsMatch(first = "", second = "") {
  const a = normalizeExternalUrl(first).replace(/\/$/, "").toLowerCase();
  const b = normalizeExternalUrl(second).replace(/\/$/, "").toLowerCase();
  return Boolean(a && b && a === b);
}

function inferEventDestinationType(form = {}) {
  const link = form.event_action_link || "";
  if (link && urlsMatch(link, form.website_url)) return "website";
  if (link && urlsMatch(link, form.menu_url)) return "menu";

  const action = resolvedEventActionText(form);
  if (action === "Reserve Table") return "reservation";
  if (["RSVP", "Get Tickets", "Join Guest List", "Save My Spot"].includes(action)) {
    return "tickets_rsvp";
  }
  if (["View Event Details", "See Event Details", "Plan My Visit"].includes(action)) {
    return link ? "event_details" : "none";
  }
  return link ? "custom" : "none";
}

function eventDestinationLink(form = {}, type = "custom") {
  if (type === "website") return normalizeExternalUrl(form.website_url);
  if (type === "menu") return normalizeExternalUrl(form.menu_url);
  if (type === "none") return "";
  return normalizeExternalUrl(form.event_action_link);
}

function eventDestinationPlaceholder(type = "custom") {
  if (type === "reservation") return "restaurant.com/reservations";
  if (type === "tickets_rsvp") return "eventbrite.com/... or restaurant.com/rsvp";
  if (type === "event_details") return "facebook.com/events/... or restaurant.com/events";
  if (type === "instagram") return "instagram.com/restaurant";
  return "www.site.com/page";
}

const EVENT_NAME_IDEAS = [
  "Live Music Night",
  "Trivia Night",
  "Game / Watch Party",
  "Tasting Event",
  "Holiday Event",
  "Family Night",
  "Guest Chef Night",
  "Special Dinner",
  "Community Event",
  "Catering Showcase",
];

const HERO_IMAGE_ACCEPTED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

const HERO_IMAGE_MAX_BYTES = 10 * 1024 * 1024;

function afterClaimNeedsLink(action = "") {
  return ["Order Online", "Reserve Table", "Join Rewards", "Custom..."].includes(
    action
  );
}

function promotionIntentText(form = {}) {
  return [
    form.offer,
    form.custom_offer,
    form.description,
    form.cta_text,
    form.custom_cta_text,
    form.campaign_label,
    form.campaign_goal_label,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isVisitOrientedPromotion(form = {}) {
  if ((form.campaign_type || "offer") === "event") return false;

  const text = promotionIntentText(form);

  return [
    "stop in",
    "visit",
    "dine-in",
    "dine in",
    "happy hour",
    "midweek",
    "weekend",
    "lunch",
    "dinner",
    "family",
    "tonight",
    "this week",
    "table",
    "in-store",
    "in store",
  ].some((phrase) => text.includes(phrase));
}

function isOrderingOrTakeoutPromotion(form = {}) {
  const text = promotionIntentText(form);

  return [
    "order online",
    "online order",
    "takeout",
    "take-out",
    "pickup",
    "pick-up",
    "delivery",
    "to-go",
    "to go",
  ].some((phrase) => text.includes(phrase));
}

function hasDedicatedOrderLink(form = {}) {
  const link = String(
    form.after_claim_button_link || form.cta_link || ""
  ).trim();

  if (!link) return false;

  if (form.website_url && urlsMatch(link, form.website_url)) {
    return false;
  }

  return true;
}

function recommendedAfterClaimAction(form = {}) {
  if ((form.campaign_type || "offer") === "event") {
    return form.after_claim_button_text || "View Menu";
  }

  if (
    isOrderingOrTakeoutPromotion(form) &&
    hasDedicatedOrderLink(form)
  ) {
    return "Order Online";
  }

  if (isVisitOrientedPromotion(form)) {
    if (form.menu_url) return "View Menu";
    if (form.address) return "Get Directions";
    if (form.website_url) return "Visit Website";
  }

  if (form.menu_url) return "View Menu";
  if (form.address) return "Get Directions";
  if (form.website_url) return "Visit Website";

  return "Custom...";
}

function shouldRepairGeneratedAfterClaim(form = {}) {
  if ((form.campaign_type || "offer") === "event") return false;

  const action = String(form.after_claim_button_text || "").trim();

  if (!action) return true;

  if (
    action === "Order Online" &&
    isVisitOrientedPromotion(form) &&
    !hasDedicatedOrderLink(form)
  ) {
    return true;
  }

  return false;
}

function resolvedAfterClaimText(form = {}) {
  return form.after_claim_button_text === "Custom..."
    ? form.custom_after_claim_button_text || "Continue"
    : form.after_claim_button_text || recommendedAfterClaimAction(form);
}

function resolvedAfterClaimLink(form = {}) {
  const action = resolvedAfterClaimText(form);

  if (action === "View Menu") {
    return form.menu_url || "";
  }

  if (action === "Get Directions") {
    return form.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          form.address
        )}`
      : "";
  }

  if (action === "Visit Website") {
    return form.website_url || "";
  }

  return String(form.after_claim_button_link || "").trim();
}

function automaticDestinationMessage(form = {}) {
  const action = resolvedAfterClaimText(form);

  if (action === "View Menu") {
    return form.menu_url
      ? `Uses the menu link automatically: ${form.menu_url}`
      : "Add the Menu URL above so this action can open the menu.";
  }

  if (action === "Get Directions") {
    return form.address
      ? `Uses the restaurant address automatically: ${form.address}`
      : "Add the restaurant address above so this action can open directions.";
  }

  if (action === "Visit Website") {
    return form.website_url
      ? `Uses the restaurant website automatically: ${form.website_url}`
      : "Add the Website URL above so this action can open the restaurant website.";
  }

  return "";
}

function afterClaimActionWarning(form = {}) {
  const action = resolvedAfterClaimText(form);

  if (
    action === "Order Online" &&
    isVisitOrientedPromotion(form) &&
    !hasDedicatedOrderLink(form)
  ) {
    return "This campaign is written to drive an in-person visit, but Order Online does not have a dedicated ordering link. View Menu or Get Directions is a better fit.";
  }

  if (
    action === "Order Online" &&
    form.after_claim_button_link &&
    form.website_url &&
    urlsMatch(form.after_claim_button_link, form.website_url)
  ) {
    return "Order Online currently points to the restaurant homepage. Use the real ordering page, or choose View Menu / Get Directions instead.";
  }

  return "";
}

function resolvedEventActionText(form = {}) {
  return form.event_action_text === "Custom..."
    ? form.custom_event_action_text || "View Event Details"
    : form.event_action_text || "View Event Details";
}

function eventActionNeedsLink(action = "") {
  return [
    "View Event Details",
    "RSVP",
    "Reserve Table",
    "Get Tickets",
    "Join Guest List",
    "Custom...",
  ].includes(action);
}

function adminUtilityButtons(form = {}) {
  const buttons = [];

  if (form.website_url) {
    buttons.push({ label: "Website", ready: true });
  }

  if (form.phone) {
    buttons.push({ label: "Call", ready: true });
  }

  if (form.address) {
    buttons.push({ label: "Directions", ready: true });
  }

  return buttons;
}

function adminEventDateLabel(value = "") {
  if (!value) return "Event date";

  try {
    return new Date(`${value}T12:00:00`).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

const FIELDS = [
  {
    key: "restaurant_name",
    label: "Restaurant Name",
    required: true,
    placeholder: "Ember & Stone Pizzeria",
  },
  {
    key: "instagram_handle",
    label: "Instagram Handle",
    placeholder: "@emberandstonepizzeria",
  },
  {
    key: "city",
    label: "City",
    placeholder: "Charleston, SC",
  },
  {
    key: "cuisine",
    label: "Food Type / Cuisine",
    placeholder: "Wood-Fired Pizzeria",
  },
  {
    key: "hero_image_url",
    label: "Hero Image URL",
    type: "text",
    placeholder: "https://…jpg",
    full: true,
  },
  {
  key: "website_url",
  label: "Website URL",
  type: "text",
  placeholder: "restaurant.com",
},
{
  key: "menu_url",
  label: "Menu URL",
  type: "text",
  placeholder: "restaurant.com/menu",
},
  {
    key: "address",
    label: "Address",
    placeholder: "Charleston, SC",
  },
  {
    key: "phone",
    label: "Phone Number",
    placeholder: "(843) 555-0131",
  },
];

const FOOD_IMAGE_PRESETS = {
  pizza:
    "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1200&q=80",
  burger:
    "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=1200&q=80",
  tacos:
    "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?auto=format&fit=crop&w=1200&q=80",
  cafe:
    "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1200&q=80",
  italian:
    "https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=1200&q=80",
  cajun:
    "https://images.unsplash.com/photo-1625944525533-473f1a3d54e7?auto=format&fit=crop&w=1200&q=80",
  hotdog:
    "https://images.unsplash.com/photo-1619740455993-9e612b1af08a?auto=format&fit=crop&w=1200&q=80",
  seafood:
    "https://images.unsplash.com/photo-1559737558-2f5a35f4523b?auto=format&fit=crop&w=1200&q=80",
  bbq:
    "https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=1200&q=80",
  chicken:
    "https://images.unsplash.com/photo-1562967914-608f82629710?auto=format&fit=crop&w=1200&q=80",
  chinese:
    "https://images.unsplash.com/photo-1496116218417-1a781b1c416c?auto=format&fit=crop&w=1200&q=80",
  sushi:
    "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?auto=format&fit=crop&w=1200&q=80",
  bakery:
    "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=1200&q=80",
  icecream:
    "https://images.unsplash.com/photo-1563805042-7684c019e1cb?auto=format&fit=crop&w=1200&q=80",
  default:
    "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1200&q=80",
};

function detectTheme(cuisine = "") {
  const c = cuisine.toLowerCase();

  if (c.includes("pizza")) return "pizza";

  if (c.includes("burger") || c.includes("hamburger")) {
    return "burger";
  }

  if (
    c.includes("taco") ||
    c.includes("mexican") ||
    c.includes("burrito")
  ) {
    return "tacos";
  }

  if (
    c.includes("coffee") ||
    c.includes("cafe") ||
    c.includes("espresso")
  ) {
    return "cafe";
  }

  if (
    c.includes("bakery") ||
    c.includes("pastry") ||
    c.includes("donut")
  ) {
    return "bakery";
  }

  if (c.includes("italian") || c.includes("pasta")) {
    return "italian";
  }

  if (
    c.includes("steak") ||
    c.includes("fine dining") ||
    c.includes("upscale")
  ) {
    return "fine_dining";
  }

  if (
    c.includes("bbq") ||
    c.includes("barbecue") ||
    c.includes("smokehouse")
  ) {
    return "bbq";
  }

  if (
    c.includes("seafood") ||
    c.includes("fish") ||
    c.includes("oyster") ||
    c.includes("crab") ||
    c.includes("lobster")
  ) {
    return "seafood";
  }

  if (c.includes("cajun") || c.includes("creole")) {
    return "cajun";
  }

  if (c.includes("chicken") || c.includes("wings")) {
    return "chicken";
  }

  if (c.includes("chinese") || c.includes("dim sum")) {
    return "chinese";
  }

  if (
    c.includes("sushi") ||
    c.includes("japanese") ||
    c.includes("ramen")
  ) {
    return "sushi";
  }

  if (
    c.includes("ice cream") ||
    c.includes("icecream") ||
    c.includes("dessert")
  ) {
    return "icecream";
  }

  if (c.includes("hot dog") || c.includes("hotdog")) {
    return "hotdog";
  }

  return "default";
}

function getOutreachStageLabel(stage) {
  const labels = {
    not_contacted: "Not Contacted",
    initial_contact: "Initial Contact",
    followup_1: "Follow-up 1",
    followup_2: "Follow-up 2",
    final_followup: "Final Follow-up",
    replied: "Replied",
    activated: "Activated",
  };

  return labels[stage] || "Outreach";
}

function isTerminalOutreachStage(stage) {
  return stage === "replied" || stage === "activated";
}

export default function MockupForm({
  mode,
  onSuccess,
  initialValues = null,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const currentUserRole = getCurrentUser()?.role;

  const [form, setForm] = useState(() =>
    isEdit
      ? { ...EMPTY }
      : {
          ...EMPTY,
          ...(initialValues || {}),
        }
  );
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState("");
  const [liveSlug, setLiveSlug] = useState("");
  const [users, setUsers] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] =
    useState(false);
  const [eventDestinationType, setEventDestinationType] =
    useState("none");
  const [adminPreviewScreen, setAdminPreviewScreen] =
    useState("primary");
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [quickAdminNote, setQuickAdminNote] = useState("");
  const [performance, setPerformance] = useState({
    page_visits: 0,
    offer_claims: 0,
    event_actions: 0,
    last_viewed_at: "",
  });
  const [campaignMeta, setCampaignMeta] = useState({
    campaign_variant: false,
    parent_mockup_id: "",
    campaign_label: "",
    campaign_goal_label: "",
    is_live_campaign: false,
  });
  const [campaignBank, setCampaignBank] = useState([]);
  const [publishingCampaign, setPublishingCampaign] = useState(false);

  const [heroUploading, setHeroUploading] = useState(false);
  const [heroDragActive, setHeroDragActive] = useState(false);

  const [outreachStage, setOutreachStage] =
    useState("not_contacted");
  const [nextFollowupAt, setNextFollowupAt] = useState("");
  const [outreachHistory, setOutreachHistory] = useState([]);
  const [outreachSaving, setOutreachSaving] = useState(false);
  const [outreachLoading, setOutreachLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;

    setLoading(true);
    setForm(EMPTY);
    setSlug("");
    setLiveSlug("");
    setPerformance({
      page_visits: 0,
      offer_claims: 0,
      event_actions: 0,
      last_viewed_at: "",
    });
    setCampaignMeta({
      campaign_variant: false,
      parent_mockup_id: "",
      campaign_label: "",
      campaign_goal_label: "",
      is_live_campaign: false,
    });
    setCampaignBank([]);
    setHasUnsavedChanges(false);

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${API}/mockups/${id}`, {
          headers: authHeaders(),
        });

        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        const loadedAfterClaimAction = shouldRepairGeneratedAfterClaim(data)
          ? recommendedAfterClaimAction(data)
          : AFTER_CLAIM_OPTIONS.includes(data.after_claim_button_text)
            ? data.after_claim_button_text
            : data.after_claim_button_text
              ? "Custom..."
              : recommendedAfterClaimAction(data);

        const loadedAfterClaimLink =
          loadedAfterClaimAction === "View Menu" ||
          loadedAfterClaimAction === "Get Directions" ||
          loadedAfterClaimAction === "Visit Website"
            ? ""
            : data.after_claim_button_link || data.cta_link || "";

        const detectedLoadedTheme = detectTheme(data.cuisine || "");

const loadedTheme =
  !data.theme || data.theme === "default"
    ? detectedLoadedTheme
    : data.theme;

const loadedThemeColor = themeRecommendedColor(loadedTheme);

const loadedOfferOptions =
  OFFER_OPTIONS_BY_THEME[loadedTheme] || OFFER_OPTIONS_BY_THEME.default;

const loadedButtonOptions =
  BUTTON_OPTIONS_BY_THEME[loadedTheme] || BUTTON_OPTIONS_BY_THEME.default;
        if (!cancelled) {
          setEventDestinationType(
            inferEventDestinationType({
              ...data,
              event_action_text:
                data.event_action_text || "View Event Details",
            })
          );

          setForm({
            restaurant_name: data.restaurant_name || "",
            instagram_handle: data.instagram_handle || "",
            city: data.city || "",
            cuisine: data.cuisine || "",
            hero_image_url: data.hero_image_url || "",
            website_url: data.website_url || "",
            menu_url: data.menu_url || "",
            address: data.address || "",
            phone: data.phone || "",
            hours: data.hours || "",
            offer:
  data.offer && loadedOfferOptions.includes(data.offer)
    ? data.offer
    : "",

custom_offer:
  data.offer && !loadedOfferOptions.includes(data.offer)
    ? data.offer
    : "",
            description: data.description || "",
           cta_text:
  data.cta_text && loadedButtonOptions.includes(data.cta_text)
    ? data.cta_text
    : "",

custom_cta_text:
  data.cta_text && !loadedButtonOptions.includes(data.cta_text)
    ? data.cta_text
    : "",
            cta_link: data.cta_link || "",
            after_claim_button_text: loadedAfterClaimAction,
            after_claim_button_link: loadedAfterClaimLink,
            offer_terms:
              data.offer_terms ||
              "Valid this week only · One per table · Not combinable with other offers",
            campaign_type: data.campaign_type || "offer",
            featured_item_name: data.featured_item_name || "",
            featured_item_description: data.featured_item_description || "",
            featured_action_text: data.featured_action_text || "View Menu",
            featured_action_link: data.featured_action_link || "",
            event_name: data.event_name || "",
            event_date: data.event_date || "",
            event_time: data.event_time || "",
            event_description: data.event_description || "",
            event_action_text: EVENT_ACTION_OPTIONS.includes(data.event_action_text)
              ? data.event_action_text
              : data.event_action_text
                ? "Custom..."
                : "View Event Details",
            event_action_link: data.event_action_link || "",
            custom_event_action_text:
              data.event_action_text &&
              !EVENT_ACTION_OPTIONS.includes(data.event_action_text)
                ? data.event_action_text
                : "",
            custom_after_claim_button_text:
              data.after_claim_button_text &&
              !AFTER_CLAIM_OPTIONS.includes(data.after_claim_button_text)
                ? data.after_claim_button_text
                : "",
           brand_color:
  !data.brand_color ||
  (data.brand_color.toUpperCase() === "#00E5FF" &&
    loadedTheme !== "default")
    ? loadedThemeColor
    : data.brand_color,

recommended_brand_color: loadedThemeColor,

theme: loadedTheme,
            owner_id: data.owner_id || "",
            owner_email: data.owner_email || "",
            assigned_to: data.assigned_to || "owner",
            notes: data.notes || "",
          });

          setSlug(data.slug || "");
          setLiveSlug(data.live_slug || "");
          setPerformance({
            page_visits: Number(data.page_visits || 0),
            offer_claims: Number(data.offer_claims || 0),
            event_actions: Number(data.event_actions || 0),
            last_viewed_at: data.last_viewed_at || "",
          });
          setCampaignMeta({
            campaign_variant: Boolean(data.campaign_variant || data.parent_mockup_id),
            parent_mockup_id: data.parent_mockup_id || "",
            campaign_label: data.campaign_label || "",
            campaign_goal_label: data.campaign_goal_label || "",
            is_live_campaign: Boolean(data.is_live_campaign),
          });
          setCampaignBank(
            Array.isArray(data.campaign_bank)
              ? data.campaign_bank
              : []
          );
          setHasUnsavedChanges(false);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to load mockup:", err);
        toast.error("Could not load restaurant page");
        navigate("/admin/mockups");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  useEffect(() => {
    if (!isEdit || !id) return;

    let cancelled = false;

    setOutreachStage("not_contacted");
    setNextFollowupAt("");
    setOutreachHistory([]);
    setOutreachLoading(true);

    (async () => {
      try {
        const res = await fetch(
          `${API}/mockups/${id}/outreach`,
          {
            headers: authHeaders(),
          }
        );

        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }

        if (!res.ok) {
          throw new Error(await res.text());
        }

        const data = await res.json();

        if (!cancelled) {
          setOutreachStage(
            data.outreach_stage || "not_contacted"
          );
          setNextFollowupAt(
            data.next_followup_at || ""
          );
          setOutreachHistory(
            Array.isArray(data.outreach_history)
              ? data.outreach_history
              : []
          );
        }
      } catch (err) {
        console.error("Failed to load outreach:", err);

        if (!cancelled) {
          setOutreachStage("not_contacted");
          setNextFollowupAt("");
          setOutreachHistory([]);
          toast.error("Could not load saved outreach progress");
        }
      } finally {
        if (!cancelled) {
          setOutreachLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener(
      "beforeunload",
      warnBeforeLeaving
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        warnBeforeLeaving
      );
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (currentUserRole !== "owner") {
      setUsers([]);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/users`, {
          headers: authHeaders(),
        });

        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }

        if (!res.ok) return;

        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error("Failed to load users", err);
        setUsers([]);
      }
    })();
  }, [currentUserRole]);

  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (
        name === "cuisine" &&
        (!prev.theme || prev.theme === "default")
      ) {
        const detectedTheme = detectTheme(value);
        const detectedColor = themeRecommendedColor(detectedTheme);
        const currentColor = adminThemeColor(prev);
        const currentRecommended =
          themeRecommendedColor(prev.theme || "default");

        return {
          ...prev,
          [name]: value,
          theme: detectedTheme,
          recommended_brand_color: detectedColor,
          brand_color:
            currentColor === currentRecommended
              ? detectedColor
              : prev.brand_color,
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });

    setHasUnsavedChanges(true);
  };

  const applyThemePreset = (themeKey = "default") => {
    const nextTheme =
      THEME_RECOMMENDED_COLORS[themeKey]
        ? themeKey
        : "default";
    const nextColor = themeRecommendedColor(nextTheme);

    setForm((prev) => ({
      ...prev,
      theme: nextTheme,
      recommended_brand_color: nextColor,
      brand_color: nextColor,
    }));

    setHasUnsavedChanges(true);
  };

  const applyFoodImage = (type = "default") => {
    const nextTheme =
      THEME_RECOMMENDED_COLORS[type]
        ? type
        : "default";
    const nextColor = themeRecommendedColor(nextTheme);

    setForm((prev) => ({
      ...prev,
      hero_image_url:
        FOOD_IMAGE_PRESETS[type] || FOOD_IMAGE_PRESETS.default,
      theme: nextTheme,
      recommended_brand_color: nextColor,
      brand_color: nextColor,
    }));

    setHasUnsavedChanges(true);
  };

  const handleHeroImageFile = async (file) => {
    if (!file || heroUploading) return;

    if (!isEdit || !id) {
      toast.error("Create the Restaurant Page before uploading an image.");
      return;
    }

    if (!HERO_IMAGE_ACCEPTED_TYPES.has(file.type)) {
      toast.error("Use a JPG, PNG, WEBP, or GIF image.");
      return;
    }

    if (file.size > HERO_IMAGE_MAX_BYTES) {
      toast.error("Image must be 10 MB or smaller.");
      return;
    }

    setHeroUploading(true);

    try {
      const signRes = await fetch(
        `${API}/mockups/${id}/media-upload-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({
            filename: file.name || "restaurant-hero",
            content_type: file.type,
            purpose: "hero",
          }),
        }
      );

      if (signRes.status === 401 || signRes.status === 403) {
        handleUnauthorized();
        return;
      }

      const signData = await signRes.json().catch(() => ({}));

      if (!signRes.ok) {
        throw new Error(
          signData?.detail || "Could not prepare image upload."
        );
      }

      const uploadRes = await fetch(signData.upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": signData.content_type || file.type,
        },
        body: file,
      });

      if (!uploadRes.ok) {
        throw new Error(
          `Image upload failed with HTTP ${uploadRes.status}.`
        );
      }

      if (!signData.public_url) {
        throw new Error("Upload completed, but no public image URL was returned.");
      }

      setForm((prev) => ({
        ...prev,
        hero_image_url: signData.public_url,
      }));
      setHasUnsavedChanges(true);

      toast.success(
        "Image uploaded. Save Save & Review Your Restaurant Page to publish it."
      );
    } catch (err) {
      console.error("Hero image upload failed:", err);
      toast.error(err?.message || "Image upload failed. Try again.");
    } finally {
      setHeroUploading(false);
      setHeroDragActive(false);
    }
  };

  const applyOutreachResponse = (data) => {
    setOutreachStage(
      data.outreach_stage || "not_contacted"
    );
    setNextFollowupAt(
      data.next_followup_at || ""
    );
    setOutreachHistory(
      Array.isArray(data.outreach_history)
        ? data.outreach_history
        : []
    );
  };

  const saveOutreachSettings = async ({
    outreachStage: stage,
    followupDate,
    successMessage,
  }) => {
    if (!isEdit || !id || outreachSaving) {
      return false;
    }

    setOutreachSaving(true);

    try {
      const res = await fetch(
        `${API}/mockups/${id}/outreach`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({
            outreach_stage: stage,
            next_followup_at: followupDate,
          }),
        }
      );

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return false;
      }

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const data = await res.json();
      applyOutreachResponse(data);

      if (successMessage) {
        toast.success(successMessage);
      }

      return true;
    } catch (err) {
      console.error("Could not save outreach settings:", err);
      toast.error(
        "Outreach progress was not saved. Try again."
      );
      return false;
    } finally {
      setOutreachSaving(false);
    }
  };

  const handleOutreachStageChange = async (stage) => {
    const previousStage = outreachStage;
    const previousDate = nextFollowupAt;
    const clearedDate = isTerminalOutreachStage(stage)
      ? ""
      : nextFollowupAt;

    setOutreachStage(stage);
    setNextFollowupAt(clearedDate);

    const saved = await saveOutreachSettings({
      outreachStage: stage,
      followupDate: clearedDate,
      successMessage: `Stage saved as ${getOutreachStageLabel(
        stage
      )}.`,
    });

    if (!saved) {
      setOutreachStage(previousStage);
      setNextFollowupAt(previousDate);
    }
  };

  const handleOutreachScheduleChange = async (dateValue) => {
    const previousDate = nextFollowupAt;

    setNextFollowupAt(dateValue);

    const saved = await saveOutreachSettings({
      outreachStage,
      followupDate: dateValue,
      successMessage: dateValue
        ? "Follow-up date saved."
        : "Follow-up date cleared.",
    });

    if (!saved) {
      setNextFollowupAt(previousDate);
    }
  };

  const handleMarkSent = async ({
    message,
    messageStyle,
    channel,
    variationNumber,
    sentStage,
    nextStage,
    nextFollowupAt: scheduledDate,
    campaignId = "",
  }) => {
    if (!isEdit || !id || outreachSaving) {
      return;
    }

    setOutreachSaving(true);

    try {
      const res = await fetch(
        `${API}/mockups/${id}/outreach/mark-sent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({
            message,
            message_style: messageStyle,
            channel,
            variation_number: variationNumber,
            sent_stage: sentStage,
            next_stage: nextStage,
            next_followup_at: scheduledDate || "",
            campaign_id: campaignId || "",
          }),
        }
      );

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const data = await res.json();
      applyOutreachResponse(data);

      if (campaignId) {
        const usedAt = new Date().toISOString();

        setCampaignBank((current) =>
          current.map((campaign) =>
            campaign?.id === campaignId
              ? {
                  ...campaign,
                  used_in_outreach: true,
                  outreach_stage_used: sentStage,
                  used_at: usedAt,
                }
              : campaign
          )
        );
      }

      if (nextStage === sentStage) {
        toast.success(
          `${getOutreachStageLabel(
            sentStage
          )} saved to outreach history.`
        );
      } else {
        toast.success(
          `Outreach saved. Stage advanced to ${getOutreachStageLabel(
            nextStage
          )}.`
        );
      }
    } catch (err) {
      console.error("Could not mark outreach sent:", err);
      toast.error(
        "Outreach was not saved. The stage was not advanced."
      );
    } finally {
      setOutreachSaving(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.restaurant_name.trim()) {
      toast.error("Restaurant Name is required");
      return;
    }

    setSaving(true);

    try {
      const url = isEdit
        ? `${API}/mockups/${id}`
        : `${API}/mockups`;

      const method = isEdit ? "PUT" : "POST";

      const {
        custom_after_claim_button_text: _customAfterClaimText,
        custom_offer: _customOffer,
        custom_cta_text: _customCtaText,
        custom_event_action_text: _customEventActionText,
        ...formForSave
      } = form;

      const afterClaimText = resolvedAfterClaimText(form);
      const afterClaimLink = resolvedAfterClaimLink(form);

      const payload = {
        ...formForSave,
        offer:
          form.custom_offer
            ? form.custom_offer
            : form.offer,
        cta_text:
          form.custom_cta_text
            ? form.custom_cta_text
            : form.cta_text,
        offer_terms:
          form.offer_terms ||
          "Valid this week only · One per table · Not combinable with other offers",
        website_url: normalizeExternalUrl(form.website_url),
        menu_url: normalizeExternalUrl(form.menu_url),
        after_claim_button_text: afterClaimText,
        after_claim_button_link: normalizeExternalUrl(afterClaimLink),
        event_action_text: resolvedEventActionText(form),
        event_action_link: eventDestinationLink(
          form,
          eventDestinationType
        ),
        // Keep the legacy action link synchronized for existing Restaurant Page logic.
        cta_link: normalizeExternalUrl(afterClaimLink),
      };

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const saved = await res.json();

      // Reconcile the event controls with the backend response so the editor
      // always shows the exact values that actually persisted.
      if (isEdit) {
        setForm((prev) => ({
          ...prev,
          website_url:
  saved.website_url ?? normalizeExternalUrl(prev.website_url),

menu_url:
  saved.menu_url ?? normalizeExternalUrl(prev.menu_url),

after_claim_button_link:
  saved.after_claim_button_link ??
  normalizeExternalUrl(prev.after_claim_button_link),

cta_link:
  saved.cta_link ??
  normalizeExternalUrl(prev.cta_link),
          campaign_type: saved.campaign_type || prev.campaign_type || "offer",
          featured_item_name: saved.featured_item_name ?? prev.featured_item_name,
          featured_item_description: saved.featured_item_description ?? prev.featured_item_description,
          featured_action_text: saved.featured_action_text ?? prev.featured_action_text,
          featured_action_link: saved.featured_action_link ?? prev.featured_action_link,
          event_name: saved.event_name ?? prev.event_name,
          event_date: saved.event_date ?? prev.event_date,
          event_time: saved.event_time ?? prev.event_time,
          event_description:
            saved.event_description ?? prev.event_description,
          event_action_text:
            saved.event_action_text || prev.event_action_text || "View Event Details",
          event_action_link:
            saved.event_action_link ?? prev.event_action_link,
          theme:
            saved.theme || prev.theme || "default",
          brand_color:
            saved.brand_color ||
            prev.brand_color ||
            "#00E5FF",
          recommended_brand_color:
            saved.recommended_brand_color ||
            prev.recommended_brand_color ||
            themeRecommendedColor(
              saved.theme || prev.theme || "default"
            ),
        }));
      }

      setHasUnsavedChanges(false);

      toast.success(
        isEdit
          ? "Restaurant page changes saved"
          : "Restaurant page created"
      );

      if (isEdit) {
        setSlug(saved.slug);
        setLiveSlug(saved.live_slug || "");
        setPerformance((prev) => ({
          page_visits:
            saved.page_visits !== undefined
              ? Number(saved.page_visits || 0)
              : prev.page_visits,
          offer_claims:
            saved.offer_claims !== undefined
              ? Number(saved.offer_claims || 0)
              : prev.offer_claims,
          event_actions:
            saved.event_actions !== undefined
              ? Number(saved.event_actions || 0)
              : prev.event_actions,
          last_viewed_at:
            saved.last_viewed_at !== undefined
              ? saved.last_viewed_at || ""
              : prev.last_viewed_at,
        }));

        if (typeof onSuccess === "function") {
          onSuccess(saved);
        }
      } else if (typeof onSuccess === "function") {
        setForm(EMPTY);
        onSuccess(saved);
      } else {
        navigate("/admin/mockups");
      }
    } catch (err) {
      console.error(err);
      toast.error("Restaurant page save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="py-20 text-center text-zinc-500"
        data-testid="mockup-form-loading"
      >
        Loading…
      </div>
    );
  }

  const demoPageUrl =
    isEdit && slug
      ? `${window.location.origin}/mockup/${slug}`
      : "";

  const liveRestaurantPageUrl =
    isEdit && liveSlug
      ? `${window.location.origin}/r/${liveSlug}`
      : "";

  const restaurantPageUrl =
    liveRestaurantPageUrl || demoPageUrl;

  const restaurantPageButtonLabel =
    liveRestaurantPageUrl
      ? "View Live Restaurant Page"
      : "View Demo Page";

  const restaurantSaveLabel = saving
    ? "Saving Restaurant Page…"
    : isEdit
      ? "Save Restaurant Page Changes"
      : "Create Restaurant Page";

  const jumpToAdminSection = (sectionId) => {
    const target = document.getElementById(sectionId);
    if (!target) return;

    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const jumpToAdminQuickEdit = () => {
    jumpToAdminSection("admin-quick-edit");
  };

  const jumpToAdminPreview = () => {
    if (window.innerWidth < 1024) {
      setMobilePreviewOpen(true);
      return;
    }

    jumpToAdminSection("admin-customer-preview");
  };

  const addQuickAdminNote = () => {
    const note = String(quickAdminNote || "").trim();
    if (!note) {
      toast.error("Choose a quick note first");
      return;
    }

    setForm((prev) => {
      const current = String(prev.notes || "").trim();

      if (current.split("\n").some((line) => line.trim() === `• ${note}`)) {
        return prev;
      }

      return {
        ...prev,
        notes: current
          ? `${current}\n• ${note}`
          : `• ${note}`,
      };
    });

    setQuickAdminNote("");
    setHasUnsavedChanges(true);
    toast.success("Quick note added");
  };

  const publishCampaignLive = async () => {
    if (!isEdit || !id || !campaignMeta.campaign_variant) return;

    if (hasUnsavedChanges) {
      toast.error("Save this campaign first, then publish it live.");
      return;
    }

    const label =
      campaignMeta.campaign_label ||
      (form.campaign_type === "event" ? "this Event campaign" : "this Promotion campaign");

    if (!window.confirm(`Publish "${label}" to the restaurant's live TapToTable Page?`)) {
      return;
    }

    setPublishingCampaign(true);
    try {
      const res = await fetch(`${API}/mockups/${id}/publish-campaign`, {
        method: "POST",
        headers: authHeaders(),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);

      toast.success(
        form.campaign_type === "event"
          ? "Event campaign is now live"
          : "Promotion campaign is now live"
      );

      if (data.live_slug) setLiveSlug(data.live_slug);

      setCampaignMeta((current) => ({
        ...current,
        is_live_campaign: true,
      }));
    } catch (err) {
      console.error("Publish campaign failed:", err);
      toast.error(err?.message || "Could not publish this campaign");
    } finally {
      setPublishingCampaign(false);
    }
  };

  const restaurantPagesPath =
    currentUserRole === "owner"
      ? "/admin/mockups"
      : "/staff/mockups";

  const performanceActionLabel =
    form.campaign_type === "event"
      ? "Event Actions"
      : "Offers Claimed";

  const performanceActions =
    form.campaign_type === "event"
      ? performance.event_actions
      : performance.offer_claims;

  const performanceRate =
    performance.page_visits > 0
      ? Math.round(
          (performanceActions / performance.page_visits) * 100
        )
      : 0;

  const performanceRateLabel =
    form.campaign_type === "event"
      ? "Action Rate"
      : "Claim Rate";

  const performanceLastViewed = performance.last_viewed_at
    ? new Date(performance.last_viewed_at).toLocaleString()
    : "Not viewed yet";

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl border border-white/5 bg-[#121214] p-6 pb-24 md:p-8 lg:pb-8"
      data-testid="mockup-form"
    >
      <Toaster theme="dark" position="top-center" />

      {isEdit ? (
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <Link
            to={restaurantPagesPath}
            className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-zinc-200 transition-colors hover:border-cyan-400/30 hover:bg-cyan-400/10 hover:text-cyan-200"
            data-testid="mockup-form-back-to-restaurants"
          >
            ← Back to Restaurant Pages
          </Link>

          <button
            type="button"
            onClick={jumpToAdminQuickEdit}
            className="inline-flex items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-200 transition-colors hover:bg-cyan-400/20"
            data-testid="mockup-form-jump-to-edit"
          >
            Edit Restaurant Page ↓
          </button>
        </div>
      ) : null}

      {!isEdit && form.source_lead_id ? (
        <div className="mb-6 rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
            Building From Inbound Lead
          </div>
          <p className="mt-1 text-sm leading-5 text-zinc-300">
            Signup information has been carried into this Restaurant Page. Review
            the details, complete anything missing, then create the page.
          </p>
        </div>
      ) : null}

      {form.restaurant_name ? (
        <div className="mb-6 rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Editing Restaurant Page
          </div>

          <div className="mt-1 font-display text-xl font-semibold text-white">
            {form.restaurant_name}
          </div>

          {form.cuisine || form.city ? (
            <div className="mt-1 text-sm text-zinc-400">
              {[form.cuisine, form.city]
                .filter(Boolean)
                .join(" · ")}
            </div>
          ) : null}
        </div>
      ) : null}

      {isEdit && slug ? (
        <div
          className="mb-6 overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04]"
          data-testid="mockup-performance-panel"
        >
          <div className="border-b border-cyan-400/15 px-5 py-4">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                  Performance
                </div>
                <h2 className="mt-1 font-display text-xl font-semibold text-white">
                  Customer Activity
                </h2>
              </div>
              <p className="text-xs text-zinc-500">
                Live Restaurant Page analytics
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-px bg-white/10 md:grid-cols-4">
            <div className="bg-[#121214] p-4">
              <div className="text-2xl font-bold text-white">
                {performance.page_visits}
              </div>
              <div className="mt-1 text-xs font-semibold text-cyan-300">
                Page Visits
              </div>
            </div>

            <div className="bg-[#121214] p-4">
              <div className="text-2xl font-bold text-white">
                {performanceActions}
              </div>
              <div className="mt-1 text-xs font-semibold text-cyan-300">
                {performanceActionLabel}
              </div>
            </div>

            <div className="bg-[#121214] p-4">
              <div className="text-2xl font-bold text-white">
                {performanceRate}%
              </div>
              <div className="mt-1 text-xs font-semibold text-cyan-300">
                {performanceRateLabel}
              </div>
            </div>

            <div className="bg-[#121214] p-4">
              <div className="text-sm font-semibold leading-5 text-white">
                {performanceLastViewed}
              </div>
              <div className="mt-1 text-xs font-semibold text-cyan-300">
                Last Viewed
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 px-5 py-3 text-xs leading-5 text-zinc-500">
            {form.campaign_type === "event"
              ? "Event Actions count taps on the restaurant’s main Event button."
              : "Offers Claimed count completed customer claim actions."}
          </div>
        </div>
      ) : null}

      <div className="grid gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
            Assigned To
          </label>

          <select
            name="assigned_to"
            value={form.assigned_to || "owner"}
            onChange={(e) => {
              setHasUnsavedChanges(true);

              if (e.target.value === "owner") {
                setForm((prev) => ({
                  ...prev,
                  assigned_to: "owner",
                  owner_id: "owner",
                  owner_email: "",
                }));
                return;
              }

              const selected = users.find(
                (user) => user.id === e.target.value
              );

              setForm((prev) => ({
                ...prev,
                assigned_to: selected?.id || "owner",
                owner_id: selected?.id || "owner",
                owner_email: selected?.email || "",
              }));
            }}
            className="w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 text-white focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
          >
            <option value="owner">Owner</option>

            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.role})
              </option>
            ))}
          </select>
        </div>

      </div>

      {isEdit && slug ? (
        <>
          <div className="mt-8 overflow-hidden rounded-2xl border border-[#00E5FF]/25 bg-[#0E1719]">
            <div className="border-b border-[#00E5FF]/20 bg-[#00E5FF]/5 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-1 rounded-full bg-[#00E5FF]" />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">
                    Quick Outreach Steps
                  </div>
                  <div className="mt-1 text-lg font-semibold text-white">
                    Work This Restaurant
                  </div>
                </div>
              </div>
            </div>

            <div className="p-5">
              <div className="grid gap-2 text-sm text-zinc-200 sm:grid-cols-2 lg:grid-cols-3">
              {[
                "1. Review Restaurant Page",
                "2. Choose follow-up date",
                "3. Generate message",
                "4. Send the message",
                "5. Click Mark Sent",
                "6. Later follow-up? Create a fresh campaign idea, then send its new Restaurant Page link",
              ].map((step) => (
                <div
                  key={step}
                  className="rounded-lg border border-white/10 bg-black/20 px-3 py-2"
                >
                  {step}
                </div>
              ))}
            </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-2">
                  <p className="text-xs leading-5 text-zinc-400">
                    Need the complete process? Open the Daily Sales Workflow to
                    work your full follow-up queue.
                  </p>
                  <p className="text-xs leading-5 text-fuchsia-200/90">
                    Follow-up tip: instead of repeating the same demo, use
                    <span className="font-semibold text-fuchsia-200"> + Create New Campaign </span>
                    on Restaurant Pages and send the fresh campaign link in a later follow-up.
                  </p>
                </div>

                <Link
                  to={restaurantPagesPath}
                  className="inline-flex flex-none items-center justify-center rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-4 py-2 text-xs font-semibold text-[#66FFFF] transition-colors hover:bg-[#00E5FF]/15"
                  data-testid="mockup-form-sales-workflow-link"
                >
                  Open Daily Sales Workflow
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-8 mb-3 flex items-center gap-3">
            <div className="h-9 w-1 rounded-full bg-[#00E5FF]" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">
                Sales Workspace
              </div>
              <h2 className="mt-0.5 font-display text-2xl font-semibold text-white">
                Outreach
              </h2>
            </div>
            <div className="ml-2 h-px flex-1 bg-gradient-to-r from-[#00E5FF]/30 to-transparent" />
          </div>

          {outreachLoading ? (
            <div className="rounded-2xl border border-white/10 bg-[#121214] px-5 py-10 text-center text-sm text-zinc-500">
              Loading saved outreach progress…
            </div>
          ) : (
            <OutreachPanel
              key={id}
              restaurantName={form.restaurant_name}
              reviewUrl={restaurantPageUrl}
              currentStage={outreachStage}
              nextFollowupAt={nextFollowupAt}
              history={outreachHistory}
              campaignBank={campaignBank}
              saving={outreachSaving}
              onStageChange={handleOutreachStageChange}
              onScheduleChange={handleOutreachScheduleChange}
              onMarkSent={handleMarkSent}
            />
          )}
        </>
      ) : null}

      <div
        id="admin-edit-top"
        className={isEdit && slug ? "mt-8 scroll-mt-6" : "mt-6 scroll-mt-6"}
      >
        <div className="mb-5 overflow-hidden rounded-2xl border border-cyan-400/20 bg-[#18181C]">
          <div className="flex items-stretch">
            <div className="w-1.5 flex-none bg-cyan-400" />
            <div className="flex-1 px-5 py-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                How This Works
              </p>
              <h2 className="mt-1 font-display text-2xl font-semibold text-white">
                Build your page. Preview it. Save when it looks right.
              </h2>
              <p className="mt-2 text-sm leading-6 text-zinc-400">
                Make changes in Page Settings. The Customer Preview updates
                instantly. Nothing changes on the saved Restaurant Page until
                you click {isEdit ? "Save Restaurant Page Changes" : "Create Restaurant Page"}.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {["1. Edit", "2. Preview", "3. Save"].map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold text-cyan-200"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          id="admin-quick-edit"
          className="mb-5 scroll-mt-6 overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.05]"
        >
          <div className="border-b border-cyan-400/15 px-5 py-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
              Quick Edit
            </div>
            <h2 className="mt-1 font-display text-xl font-semibold text-white">
              Jump Straight to What You Want to Change
            </h2>
            <p className="mt-2 text-sm leading-6 text-zinc-400">
              This follows the same editing order as the Client Dashboard.
              Admin-only food presets, notes, assignment, and outreach remain available.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["1. Promotion / Event", "admin-section-type"],
              ["2. Page Photo", "admin-section-photo"],
              ["3. Page Content", "admin-section-content"],
              ["4. Customer Action", "admin-section-action"],
              ["5. Restaurant Info", "admin-section-info"],
            ].map(([label, sectionId]) => (
              <button
                key={sectionId}
                type="button"
                onClick={() => jumpToAdminSection(sectionId)}
                className="min-h-[48px] w-full min-w-0 whitespace-normal break-words rounded-xl border border-white/10 bg-black/20 px-3 py-3 text-center text-sm font-semibold text-zinc-200 transition-colors hover:border-cyan-400/35 hover:bg-cyan-400/10 hover:text-white"
              >
                {label}
              </button>
            ))}

            <button
              type="button"
              onClick={jumpToAdminPreview}
              className="min-h-[48px] w-full min-w-0 whitespace-normal break-words rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-3 py-3 text-center text-sm font-semibold text-cyan-200 transition-colors hover:bg-cyan-400/15"
            >
              Preview
            </button>

            <button
              type="submit"
              disabled={saving}
              className="min-h-[48px] w-full min-w-0 whitespace-normal break-words rounded-xl bg-cyan-400 px-3 py-3 text-center text-sm font-bold text-black transition-colors hover:bg-cyan-300 disabled:opacity-50"
            >
              {restaurantSaveLabel}
            </button>
          </div>
        </div>

      {campaignMeta.campaign_variant ? (
        <div className="mb-6 rounded-2xl border border-fuchsia-400/30 bg-fuchsia-400/[0.07] p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-300">
                Campaign Idea
              </div>
              <div className="mt-1 font-display text-lg font-semibold text-white">
                {campaignMeta.campaign_label ||
                  (form.campaign_type === "event" ? "Event Campaign" : "Promotion Campaign")}
              </div>
              {campaignMeta.campaign_goal_label ? (
                <div className="mt-2 inline-flex rounded-full border border-fuchsia-400/25 bg-fuchsia-400/10 px-3 py-1 text-[11px] font-semibold text-fuchsia-200">
                  Goal: {campaignMeta.campaign_goal_label}
                </div>
              ) : null}

              <p className="mt-2 max-w-2xl text-xs leading-5 text-zinc-400">
                {campaignMeta.is_live_campaign
                  ? "This is the restaurant’s current live campaign. After you save, changes to this campaign update the live TapToTable Page automatically."
                  : "Save this campaign, then publish it when you want it to replace the Promotion or Event currently shown on the restaurant’s live page."}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={restaurantPagesPath}
                className="inline-flex shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-zinc-200 transition-colors hover:border-cyan-400/30 hover:bg-cyan-400/10 hover:text-cyan-200"
              >
                ← Back to Restaurant Pages
              </Link>

              {liveSlug ? (
                <a
                  href={`/r/${liveSlug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center justify-center rounded-full border border-cyan-400/35 bg-cyan-400/10 px-4 py-2.5 text-sm font-semibold text-cyan-200 transition-colors hover:bg-cyan-400/20"
                  data-testid="campaign-idea-view-live-page"
                >
                  View Live Page ↗
                </a>
              ) : null}

              {campaignMeta.is_live_campaign ? (
                <span className="inline-flex shrink-0 items-center justify-center rounded-full border border-emerald-400/35 bg-emerald-400/15 px-5 py-2.5 text-sm font-semibold text-emerald-300">
                  ✓ Current Live Campaign
                </span>
              ) : (
                <button
                  type="button"
                  onClick={publishCampaignLive}
                  disabled={publishingCampaign || hasUnsavedChanges}
                  className="inline-flex shrink-0 items-center justify-center rounded-full border border-fuchsia-400/40 bg-fuchsia-400/15 px-5 py-2.5 text-sm font-semibold text-fuchsia-200 transition-colors hover:bg-fuchsia-400/25 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {publishingCampaign ? "Publishing..." : "Publish This Campaign Live"}
                </button>
              )}
            </div>
          </div>

          {hasUnsavedChanges ? (
            <div className="mt-3 text-xs font-semibold text-amber-300">
              {campaignMeta.is_live_campaign
                ? "Save changes to update the live campaign."
                : "Save changes before publishing this campaign."}
            </div>
          ) : null}
        </div>
      ) : null}


        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
          <div className="space-y-6">
          <section id="admin-section-type" className="scroll-mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035]">
            <div className="border-b border-white/10 px-5 py-4">
              <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                1 · Choose What You’re Promoting
              </div>

              <h3 className="mt-3 font-display text-xl font-semibold text-white">
                What Are You Promoting?
              </h3>
              <p className="mt-1 text-sm leading-6 text-zinc-400">
                Pick the type of Restaurant Page you want customers to see.
                Admin and Client Dashboard now use the same page structure.
              </p>
            </div>

            <div className="grid gap-3 p-5 md:grid-cols-3">
              {[
                {
                  value: "offer",
                  title: "Offer / Promotion",
                  description:
                    "Promote a special, reward, featured item, or limited-time reason to visit.",
                },
                {
                  value: "event",
                  title: "Event",
                  description:
                    "Promote live music, a tasting, guest list, tickets, reservations, or another event.",
                },
                        {
                  value: "featured_item",
                  title: "Featured Item",
                  description:
                    "Showcase a signature dish or menu favorite without requiring a discount or claim code.",
                },
              ].map((option) => {
                const active =
                  (form.campaign_type || "offer") === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        campaign_type: option.value,
                      }));
                      setAdminPreviewScreen("primary");
                      setHasUnsavedChanges(true);
                    }}
                    className={`min-h-[132px] rounded-2xl border p-4 text-left transition-all ${
                      active
                        ? option.value === "event"
                          ? "border-violet-400/70 bg-violet-400/10 ring-1 ring-violet-400/30"
                          : "border-cyan-400/70 bg-cyan-400/10 ring-1 ring-cyan-400/30"
                        : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/[0.04]"
                    }`}
                    data-testid={`mockup-campaign-${option.value}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-base font-semibold text-white">
                        {option.title}
                      </div>

                      <div
                        className={`flex h-6 w-6 items-center justify-center rounded-full border text-xs font-bold ${
                          active
                            ? "border-white/30 bg-white text-black"
                            : "border-white/15 text-zinc-500"
                        }`}
                      >
                        {active ? "✓" : ""}
                      </div>
                    </div>

                    <p className="mt-2 text-sm leading-5 text-zinc-400">
                      {option.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>

            <div
              id="admin-section-photo"
              className="scroll-mt-6 overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04] shadow-[0_0_0_1px_rgba(34,211,238,0.03)]"
            >
              <div className="border-b border-cyan-400/15 bg-cyan-400/10 px-5 py-4">
                <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                  2 · Choose Your Page Photo
                </div>

                <div className="mt-3 flex items-center gap-3">
                  <div className="h-8 w-1 rounded-full bg-cyan-400" />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-xl font-semibold text-white">
                      Choose a Photo for Your Page
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-zinc-400">
                      Pick the main photo customers will see. Upload one, paste a
                      URL, or use one of the Admin food-image presets below.
                    </p>
                  </div>

                  {heroUploading ? (
                    <div className="text-xs font-semibold text-cyan-300">
                      Uploading…
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="p-5">

              {form.hero_image_url ? (
                <div className="mb-4 overflow-hidden rounded-xl border border-white/10 bg-black/20">
                  <img
                    src={form.hero_image_url}
                    alt={`${form.restaurant_name || "Restaurant"} hero`}
                    className="h-56 w-full object-cover"
                  />
                </div>
              ) : null}

              <div
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!heroUploading && isEdit) setHeroDragActive(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!heroUploading && isEdit) setHeroDragActive(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setHeroDragActive(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setHeroDragActive(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleHeroImageFile(file);
                }}
                className={`rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
                  heroDragActive
                    ? "border-[#00E5FF] bg-[#00E5FF]/10"
                    : "border-white/15 bg-black/15"
                }`}
                data-testid="mockup-hero-image-dropzone"
              >
                <div className="text-sm font-semibold text-white">
                  {isEdit
                    ? "Drag & drop a restaurant image here"
                    : "Create the Restaurant Page before uploading an image"}
                </div>

                {isEdit ? (
                  <>
                    <div className="mt-1 text-xs text-zinc-500">
                      or choose a file from your computer or phone
                    </div>

                    <label
                      className={`mt-4 inline-flex cursor-pointer items-center justify-center rounded-xl px-5 py-3 text-sm font-bold transition-colors ${
                        heroUploading
                          ? "cursor-not-allowed bg-zinc-700 text-zinc-400"
                          : "bg-[#00E5FF] text-black hover:bg-[#66FFFF]"
                      }`}
                    >
                      {heroUploading ? "Uploading Image…" : "Choose Image"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        disabled={heroUploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleHeroImageFile(file);
                          e.target.value = "";
                        }}
                        data-testid="mockup-hero-image-file"
                      />
                    </label>
                  </>
                ) : null}
              </div>

              <div className="mt-4">
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                  Hero Image URL
                </label>
                <input
                  type="url"
                  name="hero_image_url"
                  value={form.hero_image_url || ""}
                  onChange={onChange}
                  placeholder="https://…jpg"
                  data-testid="mockup-input-hero_image_url"
                  className="w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 transition-all focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
                />
                <p className="mt-2 text-xs leading-5 text-zinc-500">
                  You can still paste an image URL manually. Uploaded images are stored
                  in TapToTable media storage.
                </p>
              </div>

              <div className="mt-5 border-t border-white/10 pt-5">
                <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                  Quick Food Image
                </div>
                <p className="mb-3 text-xs leading-5 text-zinc-500">
                  Or use one of the existing TapToTable food-image presets.
                </p>

                <div className="flex flex-wrap gap-2">
                  {[
                    ["default", "General"],
                    ["pizza", "Pizza"],
                    ["burger", "Burger"],
                    ["tacos", "Tacos"],
                    ["cafe", "Cafe"],
                    ["italian", "Italian"],
                    ["cajun", "Cajun"],
                    ["hotdog", "Hot Dog"],
                    ["seafood", "Seafood"],
                    ["bbq", "BBQ"],
                    ["chicken", "Chicken"],
                    ["chinese", "Chinese"],
                    ["sushi", "Sushi"],
                    ["bakery", "Bakery"],
                    ["icecream", "Ice Cream"],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => applyFoodImage(key)}
                      className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="mt-5 border-t border-white/10 pt-5">
                  <label className="mb-2 block text-sm font-semibold text-zinc-200">
                    Admin Theme Preset
                  </label>
                  <p className="mb-2 text-xs leading-5 text-zinc-500">
                    This controls restaurant-specific suggestions and the
                    recommended page color. Food preset buttons update it too.
                  </p>
                  <select
                    value={form.theme || "default"}
                    onChange={(e) => applyThemePreset(e.target.value)}
                    data-testid="mockup-input-theme"
                    className="w-full rounded-xl border border-cyan-400/30 bg-[#1A1A1F] px-4 py-3 text-white transition-all focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  >
                    {THEME_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              </div>
            </div>

            <section className="overflow-hidden rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04]">
              <button
                type="button"
                onClick={() => setAppearanceOpen((open) => !open)}
                className="w-full px-5 py-4 text-left"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                      Page Appearance
                    </div>
                    <h3 className="mt-2 font-display text-xl font-semibold text-white">
                      Choose Your Page Color
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-zinc-400">
                      TapToTable recommends a color for the restaurant, but you
                      can change it anytime.
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-xs text-zinc-200">
                        <span
                          className="h-3.5 w-3.5 rounded-full border border-white/20"
                          style={{ backgroundColor: adminThemeColor(form) }}
                        />
                        Current: {adminColorName(adminThemeColor(form))}
                      </span>

                      <span className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1.5 text-xs text-cyan-100">
                        <span
                          className="h-3.5 w-3.5 rounded-full border border-white/20"
                          style={{
                            backgroundColor:
                              form.recommended_brand_color ||
                              themeRecommendedColor(form.theme),
                          }}
                        />
                        TapToTable Recommended:{" "}
                        {adminColorName(
                          form.recommended_brand_color ||
                            themeRecommendedColor(form.theme)
                        )}
                      </span>
                    </div>
                  </div>

                  <span className="flex-none pt-1 text-sm font-semibold text-cyan-300">
                    {appearanceOpen ? "Hide ▲" : "Edit Color ▼"}
                  </span>
                </div>
              </button>

              {appearanceOpen ? (
                <div className="border-t border-cyan-400/15 p-5">
                  <div className="grid gap-3 sm:grid-cols-4">
                    {ADMIN_COLOR_PRESETS.map(([label, color]) => {
                      const active =
                        adminThemeColor(form).toLowerCase() === color.toLowerCase();

                      return (
                        <button
                          key={color}
                          type="button"
                          onClick={() => {
                            setForm((prev) => ({
                              ...prev,
                              brand_color: color,
                            }));
                            setHasUnsavedChanges(true);
                          }}
                          className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition-all ${
                            active
                              ? "border-white/40 bg-white/10 ring-1 ring-white/20"
                              : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                          }`}
                          data-testid={`mockup-color-${label.toLowerCase()}`}
                        >
                          <span
                            className="h-7 w-7 flex-none rounded-lg border border-white/15"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-sm font-semibold text-white">
                            {label}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 p-4">
                    <label className="mb-3 block text-sm font-semibold text-zinc-200">
                      Custom Color
                    </label>

                    <div className="grid gap-3 sm:grid-cols-[88px_minmax(0,1fr)]">
                      <input
                        type="color"
                        value={adminThemeColor(form)}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            brand_color: e.target.value.toUpperCase(),
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        className="h-12 w-full cursor-pointer rounded-xl border border-white/10 bg-zinc-900 p-1"
                        aria-label="Choose custom page color"
                      />

                      <input
                        value={form.brand_color || ""}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            brand_color: e.target.value.toUpperCase(),
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        placeholder="#00E5FF"
                        data-testid="mockup-input-brand_color"
                        className="w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 font-mono text-white placeholder-zinc-500 transition-all focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                      />
                    </div>
                  </div>

                  <p className="mt-4 text-xs leading-5 text-zinc-500">
                    Your TapToTable recommendation stays on record even if you
                    choose a different color.
                  </p>
                </div>
              ) : null}
            </section>

          {form.campaign_type === "event" ? (
            <div className="contents">
            <section id="admin-section-content" className="scroll-mt-6 overflow-hidden rounded-2xl border border-violet-400/25 bg-violet-400/[0.04]">
              <div className="border-b border-violet-400/20 bg-violet-400/5 px-5 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                  3 · Create Your Event Page
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold text-white">
                  Add the Event Details
                </h3>
                <p className="mt-1 text-sm leading-6 text-zinc-400">
                  Add your event message, date, time, details, and the main
                  action customers should take.
                </p>
              </div>

              <div className="grid gap-4 p-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                    Event Name
                  </label>
                  <input
                    name="event_name"
                    value={form.event_name || ""}
                    onChange={onChange}
                    placeholder="Saturday Night Live Music"
                    className="w-full rounded-xl border border-violet-400/25 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  />

                  <div className="mt-3">
                    <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                      Quick Event Ideas
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {EVENT_NAME_IDEAS.map((idea) => (
                        <button
                          key={idea}
                          type="button"
                          onClick={() => {
                            setForm((prev) => ({
                              ...prev,
                              event_name: idea,
                            }));
                            setHasUnsavedChanges(true);
                          }}
                          className="rounded-full border border-violet-400/20 bg-violet-400/5 px-3 py-2 text-xs font-semibold text-violet-100 transition-colors hover:bg-violet-400/10"
                        >
                          {idea}
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs leading-5 text-zinc-500">
                      Click an idea to use it, then edit the Event Name however you like.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                    Event Date
                  </label>
                  <input
                    type="date"
                    name="event_date"
                    value={form.event_date || ""}
                    onChange={onChange}
                    onClick={(e) => e.currentTarget.showPicker?.()}
                    className="w-full cursor-pointer rounded-xl border border-violet-400/25 bg-[#1A1A1F] px-4 py-3 text-white focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  />
                  <p className="mt-2 text-xs text-zinc-500">
                    Click anywhere in the date box to open the calendar.
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                    Event Time
                  </label>

                  <div className="grid grid-cols-1 gap-4">
                    {[
                      ["Starts", "startHour", "startMinute", "startMeridiem"],
                      ["Ends", "endHour", "endMinute", "endMeridiem"],
                    ].map(([label, hourKey, minuteKey, meridiemKey]) => (
                      <div
                        key={label}
                        className="rounded-2xl border border-violet-400/20 bg-black/15 p-4 sm:p-5"
                      >
                        <div className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-violet-200">
                          {label}
                        </div>

                        <div className="grid grid-cols-[1fr_1.15fr_1fr] gap-3">
                          <select
                            value={parseEventTime(form.event_time)[hourKey]}
                            onChange={(e) => {
                              setForm((prev) => ({
                                ...prev,
                                event_time: updateEventTime(
                                  prev.event_time,
                                  hourKey,
                                  e.target.value
                                ),
                              }));
                              setHasUnsavedChanges(true);
                            }}
                            className="min-h-[52px] min-w-0 w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-2 py-3 text-center text-base font-semibold text-white [color-scheme:dark] sm:px-3"
                          >
                            {EVENT_HOURS.map((hour) => (
                              <option key={hour} value={hour}>
                                {hour}
                              </option>
                            ))}
                          </select>

                          <select
                            value={parseEventTime(form.event_time)[minuteKey]}
                            onChange={(e) => {
                              setForm((prev) => ({
                                ...prev,
                                event_time: updateEventTime(
                                  prev.event_time,
                                  minuteKey,
                                  e.target.value
                                ),
                              }));
                              setHasUnsavedChanges(true);
                            }}
                            className="min-h-[52px] min-w-0 w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-2 py-3 text-center text-base font-semibold text-white [color-scheme:dark] sm:px-3"
                          >
                            {EVENT_MINUTES.map((minute) => (
                              <option key={minute} value={minute}>
                                :{minute}
                              </option>
                            ))}
                          </select>

                          <select
                            value={parseEventTime(form.event_time)[meridiemKey]}
                            onChange={(e) => {
                              setForm((prev) => ({
                                ...prev,
                                event_time: updateEventTime(
                                  prev.event_time,
                                  meridiemKey,
                                  e.target.value
                                ),
                              }));
                              setHasUnsavedChanges(true);
                            }}
                            className="min-h-[52px] min-w-0 w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-2 py-3 text-center text-base font-semibold text-white [color-scheme:dark] sm:px-3"
                          >
                            {EVENT_MERIDIEMS.map((period) => (
                              <option key={period} value={period}>
                                {period}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="mt-2 text-xs text-zinc-500">
                    {form.event_time || "Choose a start and end time."}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                    Event Description
                  </label>
                  <textarea
                    rows={4}
                    name="event_description"
                    value={form.event_description || ""}
                    onChange={onChange}
                    placeholder="Live music, BBQ, drinks, and outdoor seating."
                    className="w-full resize-none rounded-xl border border-violet-400/25 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  />
                </div>

              </div>
            </section>

            <section id="admin-section-action" className="scroll-mt-6 overflow-hidden rounded-2xl border border-violet-400/25 bg-violet-400/[0.04]">
              <div className="border-b border-violet-400/15 bg-violet-400/5 px-5 py-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                  4 · Choose the Main Event Button
                </div>
                <h3 className="mt-1 font-display text-xl font-semibold text-white">
                  What Should Customers Do?
                </h3>
                <p className="mt-1 text-xs leading-5 text-zinc-400">
                  Choose the main button customers should tap and where it should take them.
                </p>
                <p className="mt-2 hidden text-xs font-semibold leading-5 text-violet-300 lg:block">
                  Preview tip: click <span className="text-white">After Button Tap</span> in the preview on the right to check the customer's next step.
                </p>
                <p className="mt-2 text-xs font-semibold leading-5 text-violet-300 lg:hidden">
                  Preview tip: use the preview below and choose <span className="text-white">After Button Tap</span>.
                </p>
              </div>

              <div className="space-y-5 p-5">
                <div>
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                    Main Event Button
                  </label>
                  <select
                    name="event_action_text"
                    value={form.event_action_text || "View Event Details"}
                    onChange={(e) => {
                      const value = e.target.value;
                      onChange(e);

                      if (value !== "Custom...") {
                        setForm((prev) => ({
                          ...prev,
                          custom_event_action_text: "",
                        }));
                      }

                      if (value === "Reserve Table") {
                        setEventDestinationType("reservation");
                      } else if (
                        ["RSVP", "Get Tickets", "Join Guest List", "Save My Spot"].includes(value)
                      ) {
                        setEventDestinationType("tickets_rsvp");
                      } else if (
                        ["View Event Details", "See Event Details", "Plan My Visit"].includes(value)
                      ) {
                        setEventDestinationType((current) =>
                          ["website", "menu"].includes(current)
                            ? current
                            : "event_details"
                        );
                      }
                    }}
                    data-testid="mockup-input-event-action-text"
                    className="w-full rounded-xl border border-violet-400/25 bg-[#1A1A1F] px-4 py-3 text-white focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  >
                    {EVENT_ACTION_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  {form.event_action_text === "Custom..." ? (
                    <div className="mt-4">
                      <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                        Custom Event Button Text
                      </label>
                      <input
                        value={form.custom_event_action_text || ""}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            custom_event_action_text: e.target.value,
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        placeholder="See Saturday's Lineup"
                        className="w-full rounded-xl border border-violet-400/25 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                      />
                    </div>
                  ) : null}
                </div>

                <div className="rounded-2xl border border-fuchsia-400/20 bg-black/20 p-4">
                  <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-violet-300">
                    Where This Button Goes
                  </label>
                  <p className="mb-3 text-xs leading-5 text-zinc-500">
                    Pick a destination. TapToTable can use the saved website or menu automatically, or you can paste another link.
                  </p>

                  <select
                    value={eventDestinationType}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setEventDestinationType(nextType);
                      setHasUnsavedChanges(true);

                      if (nextType === "website") {
                        setForm((prev) => ({
                          ...prev,
                          event_action_link: normalizeExternalUrl(
                            prev.website_url
                          ),
                        }));
                      } else if (nextType === "menu") {
                        setForm((prev) => ({
                          ...prev,
                          event_action_link: normalizeExternalUrl(
                            prev.menu_url
                          ),
                        }));
                      } else if (nextType === "none") {
                        setForm((prev) => ({
                          ...prev,
                          event_action_link: "",
                        }));
                      }
                    }}
                    className="w-full rounded-xl border border-violet-400/30 bg-[#1A1A1F] px-4 py-3 text-white focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                  >
                    {EVENT_DESTINATION_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>

                  {eventDestinationType === "website" ? (
                    <div className="mt-3 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3">
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-300">
                        Uses Restaurant Website Automatically
                      </div>
                      <p className="mt-1 break-all text-xs leading-5 text-zinc-300">
                        {form.website_url
                          ? normalizeExternalUrl(form.website_url)
                          : "Add a website in Restaurant Info first."}
                      </p>
                    </div>
                  ) : eventDestinationType === "menu" ? (
                    <div className="mt-3 rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3">
                      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-300">
                        Uses Menu Link Automatically
                      </div>
                      <p className="mt-1 break-all text-xs leading-5 text-zinc-300">
                        {form.menu_url
                          ? normalizeExternalUrl(form.menu_url)
                          : "Add a menu link in Restaurant Info first."}
                      </p>
                    </div>
                  ) : eventDestinationType === "none" ? (
                    <div className="mt-3 rounded-xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-xs leading-5 text-amber-200">
                      No destination is set yet. The button can remain visible while you finish the event setup.
                    </div>
                  ) : (
                    <div className="mt-3">
                      <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">
                        Destination Link
                      </label>
                      <input
                        value={form.event_action_link || ""}
                        onChange={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            event_action_link: e.target.value,
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        onBlur={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            event_action_link: normalizeExternalUrl(
                              e.target.value
                            ),
                          }));
                        }}
                        placeholder={eventDestinationPlaceholder(
                          eventDestinationType
                        )}
                        className="w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                      />
                      <p className="mt-2 text-xs leading-5 text-zinc-500">
                        You can enter <span className="text-zinc-300">www.site.com</span> or a full <span className="text-zinc-300">https://</span> address. TapToTable adds https:// automatically when needed.
                      </p>
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                    Automatic Customer Buttons
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {["Website", "Call", "Directions"].map((label) => {
                      const ready = adminUtilityButtons(form).some(
                        (button) => button.label === label
                      );

                      return (
                        <div
                          key={label}
                          className={`rounded-lg border px-2 py-2.5 text-center text-xs font-semibold ${
                            ready
                              ? "border-white/10 bg-white/5 text-white"
                              : "border-white/5 bg-black/20 text-zinc-600"
                          }`}
                        >
                          {label}
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs leading-5 text-zinc-500">
                    These are created automatically from Restaurant Info.
                  </p>
                </div>
              </div>
            </section>
            </div>

          ) : form.campaign_type === "featured_item" ? (
            <section
              id="admin-section-content"
              className="scroll-mt-6 rounded-2xl border border-amber-400/25 bg-amber-400/[0.04] p-6 space-y-5"
            >
              <h3 className="text-xl font-semibold text-white">
                3 · Create Your Featured Item Page
              </h3>
              <p className="text-sm text-zinc-400">
                Showcase a signature dish or menu favorite. No discounts
                or claim codes are required.
              </p>

              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-300">
                  Featured Item Name
                </label>
                <input
                  name="featured_item_name"
                  value={form.featured_item_name || ""}
                  onChange={onChange}
                  placeholder="Signature Bacon Cheeseburger"
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-300">
                  Featured Item Description
                </label>
                <textarea
                  name="featured_item_description"
                  value={form.featured_item_description || ""}
                  onChange={onChange}
                  rows={4}
                  placeholder="Describe what makes this item special."
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-300">
                  Customer Button Text
                </label>
                <input
                  name="featured_action_text"
                  value={form.featured_action_text || ""}
                  onChange={onChange}
                  placeholder="View Menu"
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-white"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-300">
                  Customer Button Link
                </label>
                <input
                  name="featured_action_link"
                  value={form.featured_action_link || ""}
                  onChange={onChange}
                  placeholder="https://restaurant.com/menu"
                  className="w-full rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-white"
                />
              </div>
            </section>
          ) : (

          <div className="space-y-5">
            <div
              id="admin-section-content"
              className="scroll-mt-6 rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04] p-6 space-y-5 shadow-[0_0_0_1px_rgba(34,211,238,0.03)]"
            >
              <div className="overflow-hidden rounded-2xl border border-cyan-400/30 bg-cyan-400/10">
                <div className="border-b border-cyan-400/15 px-4 py-3">
                  <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                    3 · Before Claim
                  </div>
                </div>

                <div className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-1 rounded-full bg-cyan-400" />
                    <h3 className="font-display text-xl font-semibold text-white">
                      Edit What Customers See Before Claiming
                    </h3>
                    <div className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
                  </div>
                  <p className="mt-2 text-sm text-zinc-400">
                    These are the customer-facing details on the first page,
                    before the customer claims the offer.
                  </p>
                </div>
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 text-sm text-zinc-300">
                  <span className="font-bold text-cyan-300" aria-hidden>→</span>
                  Restaurant Name
                </label>
                <input
                  name="restaurant_name"
                  value={form.restaurant_name || ""}
                  onChange={onChange}
                  data-testid="mockup-content-restaurant-name"
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-200">
                  <span className="font-bold text-cyan-300" aria-hidden>→</span>
                  Promotion Message
                </label>
               <select
  value={
    promotionMessageChoicesFor(form).includes(form.description)
      ? form.description
      : "Custom..."
  }
  onChange={(e) => {
    const nextValue = e.target.value;

    setForm((prev) => ({
      ...prev,
      description:
        nextValue === "Custom..."
          ? ""
          : nextValue,
    }));

    setHasUnsavedChanges(true);
  }}
  data-testid="mockup-input-description-select"
  className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
>
  {promotionMessageChoicesFor(form).map((option) => (
    <option key={option} value={option}>
      {option}
    </option>
  ))}
  <option value="Custom...">Custom Message...</option>
</select>

{!promotionMessageChoicesFor(form).includes(form.description) ? (
  <textarea
    rows={4}
    name="description"
    value={form.description || ""}
    onChange={onChange}
    placeholder="Write a custom promotion message..."
    data-testid="mockup-input-description"
    className="mt-3 w-full resize-none rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 focus:border-cyan-400 focus:outline-none"
  />
) : null}
                <p className="mt-2 text-xs leading-5 text-zinc-500">
                  Tell customers what you are promoting and why they should act.
                  This appears under “Your Promotion” in the Customer Preview.
                </p>
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-200">
                  <span className="font-bold text-cyan-300" aria-hidden>→</span>
                  Offer Customers Claim
                </label>
                <p className="mb-2 text-xs leading-5 text-zinc-500">
                  The main reason customers will tap and claim.
                </p>

                <select
                  value={
                    form.custom_offer
                      ? "Custom..."
                      : offerChoicesFor(form).includes(form.offer)
                        ? form.offer
                        : "Custom..."
                  }
                  onChange={(e) => {
                    const nextValue = e.target.value;

                    if (nextValue === "Custom...") {
                      setForm((prev) => ({
                        ...prev,
                        custom_offer: prev.custom_offer || prev.offer || "",
                      }));
                    } else {
                      setForm((prev) => ({
                        ...prev,
                        offer: nextValue,
                        custom_offer: "",
                      }));
                    }

                    setHasUnsavedChanges(true);
                  }}
                  data-testid="mockup-input-offer-select"
                  className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
                >
                  {offerChoicesFor(form).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                  <option value="Custom...">Custom Offer...</option>
                </select>

                {form.custom_offer ? (
                  <div className="mt-3">
                    <label className="mb-2 block text-sm text-zinc-300">
                      Your Custom Offer
                    </label>
                    <input
                      value={form.custom_offer}
                      onChange={(e) => {
                        setForm((prev) => ({
                          ...prev,
                          custom_offer: e.target.value,
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Type a custom offer"
                      data-testid="mockup-input-custom-offer"
                      className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 focus:border-cyan-400 focus:outline-none"
                    />
                  </div>
                ) : null}
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-200">
                  <span className="font-bold text-cyan-300" aria-hidden>→</span>
                  Claim Button
                </label>
                <p className="mb-2 text-xs leading-5 text-zinc-500">
                  This is the button customers tap to claim the offer and receive
                  their code. Pick a ready-made option or choose Custom.
                </p>

                <select
                  value={
                    form.custom_cta_text
                      ? "Custom..."
                      : buttonChoicesFor(form).includes(form.cta_text)
                        ? form.cta_text
                        : "Custom..."
                  }
                  onChange={(e) => {
                    const nextValue = e.target.value;

                    if (nextValue === "Custom...") {
                      setForm((prev) => ({
                        ...prev,
                        custom_cta_text:
                          prev.custom_cta_text || prev.cta_text || "",
                      }));
                    } else {
                      setForm((prev) => ({
                        ...prev,
                        cta_text: nextValue,
                        custom_cta_text: "",
                      }));
                    }

                    setHasUnsavedChanges(true);
                  }}
                  data-testid="mockup-input-cta-text-select"
                  className="w-full rounded-xl border-2 border-cyan-400/60 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
                >
                  {buttonChoicesFor(form).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                  <option value="Custom...">Custom...</option>
                </select>

                {form.custom_cta_text ? (
                  <div className="mt-3">
                    <label className="mb-2 block text-sm text-zinc-300">
                      Your Custom Main Button
                    </label>
                    <input
                      value={form.custom_cta_text}
                      onChange={(e) => {
                        setForm((prev) => ({
                          ...prev,
                          custom_cta_text: e.target.value,
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Type custom button text"
                      data-testid="mockup-input-custom-cta-text"
                      className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 focus:border-cyan-400 focus:outline-none"
                    />
                  </div>
                ) : null}
              </div>
            </div>

            <section id="admin-section-action" className="scroll-mt-6 overflow-hidden rounded-2xl border border-green-400/25 bg-green-400/[0.04] shadow-[0_0_0_1px_rgba(74,222,128,0.03)]">
              <div className="border-b border-green-400/15 bg-green-400/10 px-5 py-4">
                <div className="inline-flex items-center rounded-full border border-green-400/30 bg-green-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-green-300">
                  4 · After Claim
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold text-white">
                  Edit What Customers See After Claiming
                </h3>
                <p className="mt-1 text-sm leading-6 text-zinc-400">
                  This controls the main action customers see after receiving their claim code.
                  TapToTable recommends an action that matches the campaign and uses Restaurant Info automatically whenever possible.
                </p>
              </div>

              <div className="space-y-4 p-5">
                <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/10 px-4 py-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-300">
                    Recommended for this campaign
                  </div>
                  <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-white">
                        {recommendedAfterClaimAction(form)}
                      </div>
                      <p className="mt-1 text-xs leading-5 text-zinc-400">
                        {isVisitOrientedPromotion(form)
                          ? "This campaign is written to drive an in-person visit, so the second page should help the customer plan that visit."
                          : isOrderingOrTakeoutPromotion(form)
                            ? "This campaign mentions ordering or takeout, so Order Online is recommended only when a dedicated ordering link exists."
                            : "TapToTable chooses the most useful available restaurant destination."}
                      </p>
                    </div>

                    {resolvedAfterClaimText(form) !== recommendedAfterClaimAction(form) ? (
                      <button
                        type="button"
                        onClick={() => {
                          const nextAction = recommendedAfterClaimAction(form);
                          setForm((prev) => ({
                            ...prev,
                            after_claim_button_text: nextAction,
                            after_claim_button_link:
                              ["View Menu", "Get Directions", "Visit Website"].includes(nextAction)
                                ? ""
                                : prev.after_claim_button_link,
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        className="shrink-0 rounded-lg border border-cyan-400/30 bg-cyan-400/15 px-3 py-2 text-xs font-bold text-cyan-200 hover:bg-cyan-400/20"
                      >
                        Use Recommended
                      </button>
                    ) : null}
                  </div>
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                    <span className="text-sm font-bold text-cyan-300" aria-hidden>→</span>
                    Main Button After Claim
                  </label>
                  <select
                    value={form.after_claim_button_text || "Visit Restaurant"}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        after_claim_button_text: nextValue,
                        after_claim_button_link:
                          ["View Menu", "Get Directions", "Visit Website"].includes(nextValue)
                            ? ""
                            : prev.after_claim_button_link,
                        ...(nextValue === "Custom..."
                          ? {
                              custom_after_claim_button_text:
                                prev.custom_after_claim_button_text || "",
                            }
                          : {}),
                      }));
                      setHasUnsavedChanges(true);
                    }}
                    data-testid="mockup-input-after_claim_button_text"
                    className="w-full rounded-xl border border-emerald-400/30 bg-[#1A1A1F] px-4 py-3 text-white focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  >
                    {AFTER_CLAIM_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {form.after_claim_button_text === "Custom..." ? (
                  <div>
                    <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                      Custom Button Text
                    </label>
                    <input
                      value={form.custom_after_claim_button_text || ""}
                      onChange={(e) => {
                        setForm((prev) => ({
                          ...prev,
                          custom_after_claim_button_text: e.target.value,
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Order Catering"
                      data-testid="mockup-input-custom_after_claim_button_text"
                      className="w-full rounded-xl border border-emerald-400/30 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    />
                  </div>
                ) : null}

                {afterClaimNeedsLink(form.after_claim_button_text) ? (
                  <div>
                    <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                      Where This Button Goes
                    </label>
                    <p className="mb-2 text-xs leading-5 text-zinc-500">
                      Paste the exact page this button should open. For Join Rewards, use the restaurant's rewards signup page.
                    </p>
                    <input
                      type="text"
                      value={form.after_claim_button_link || ""}
                      onChange={(e) => {
                        setForm((prev) => ({
                          ...prev,
                          after_claim_button_link: e.target.value,
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="https://..."
                      data-testid="mockup-input-after_claim_button_link"
                      className="w-full rounded-xl border border-emerald-400/30 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    />
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-300">
                      Destination handled automatically
                    </div>
                    <p className="mt-1 break-words text-xs leading-5 text-zinc-300">
                      {automaticDestinationMessage(form)}
                    </p>
                  </div>
                )}

                {afterClaimActionWarning(form) ? (
                  <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-300">
                      Check this action
                    </div>
                    <p className="mt-1 text-xs leading-5 text-amber-100/90">
                      {afterClaimActionWarning(form)}
                    </p>
                  </div>
                ) : null}

                <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                    After Claim Will Show
                  </div>
                  <div className="mt-1 text-sm font-semibold text-white">
                    {resolvedAfterClaimText(form)}
                  </div>
                  <div className="mt-1 break-words text-xs text-zinc-500">
                    {resolvedAfterClaimLink(form) || "No destination link is available yet."}
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-4">
                  <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                    Automatic Customer Buttons
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {["Website", "Call", "Directions"].map((label) => {
                      const ready = adminUtilityButtons(form).some(
                        (button) => button.label === label
                      );

                      return (
                        <div
                          key={label}
                          className={`rounded-lg border px-2 py-2.5 text-center text-xs font-semibold ${
                            ready
                              ? "border-white/10 bg-white/5 text-white"
                              : "border-white/5 bg-black/20 text-zinc-600"
                          }`}
                        >
                          {label}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-cyan-200">
                    <span className="font-bold text-cyan-300" aria-hidden>→</span>
                    Offer Terms
                  </label>
                  <p className="mb-2 text-xs leading-5 text-zinc-500">
                    Keep redemption rules short and easy for customers and staff to understand.
                  </p>

                  <select
                    value={
                      OFFER_TERMS_OPTIONS.includes(form.offer_terms)
                        ? form.offer_terms
                        : "Custom..."
                    }
                    onChange={(e) => {
                      setForm((prev) => ({
                        ...prev,
                        offer_terms:
                          e.target.value === "Custom..."
                            ? ""
                            : e.target.value,
                      }));
                      setHasUnsavedChanges(true);
                    }}
                    className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
                    data-testid="mockup-input-offer-terms-select"
                  >
                    {OFFER_TERMS_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <textarea
                    value={form.offer_terms || ""}
                    onChange={(e) => {
                      setForm((prev) => ({
                        ...prev,
                        offer_terms: e.target.value,
                      }));
                      setHasUnsavedChanges(true);
                    }}
                    rows={3}
                    placeholder="Enter offer terms..."
                    className="mt-3 w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
                    data-testid="mockup-input-offer-terms"
                  />
                </div>
              </div>
            </section>
          </div>
          )}


          </div>

          <aside id="admin-customer-preview" className="hidden scroll-mt-6 lg:block lg:sticky lg:top-6">
            <AdminLiveUpdatePreview
              form={form}
              screen={adminPreviewScreen}
              onScreenChange={setAdminPreviewScreen}
              eventDestinationType={eventDestinationType}
            />
          </aside>
        </div>

        <div className="mt-8 space-y-6">
                    <div id="admin-section-info" className="scroll-mt-6 w-full">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <button
                type="button"
                onClick={() => setDetailsOpen((open) => !open)}
                className="flex w-full items-center justify-between gap-4 text-left"
              >
                <div className="flex flex-1 items-center gap-3">
                  <div className="h-8 w-1 rounded-full bg-cyan-400" />
                  <div>
                    <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
                      5 · Restaurant Info
                    </div>
                    <h3 className="mt-1 font-display text-xl font-semibold text-white">
                      Keep Your Restaurant Info Current
                    </h3>
                    <p className="mt-1 text-sm text-zinc-400">
                      Name, contact details, links, location, cuisine, and hours.
                    </p>
                  </div>
                </div>

                <span className="flex-none text-sm font-semibold text-cyan-300">
                  {detailsOpen ? "Hide ▲" : "Edit ▼"}
                </span>
              </button>

              {detailsOpen ? (
                <div className="mt-5 grid gap-5 border-t border-white/10 pt-5 md:grid-cols-2">
                  {FIELDS.slice(0, 4).map((field) => {
                    const common = {
                      name: field.key,
                      value: form[field.key],
                      onChange,
                      placeholder: field.placeholder,
                      "data-testid": `mockup-input-${field.key}`,
                      className:
                        "w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 transition-all focus:border-cyan-400 focus:outline-none",
                    };

                    return (
                      <div key={field.key}>
                        <label className="mb-2 block text-sm font-semibold text-zinc-300">
                          {field.label}
                          {field.required ? (
                            <span className="text-cyan-300">*</span>
                          ) : null}
                        </label>
                        <input type={field.type || "text"} {...common} />
                      </div>
                    );
                  })}

                  {FIELDS.slice(5).map((field) => {
                    const common = {
                      name: field.key,
                      value: form[field.key],
                      onChange,
                      placeholder: field.placeholder,
                      "data-testid": `mockup-input-${field.key}`,
                      className:
                        "w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 transition-all focus:border-cyan-400 focus:outline-none",
                    };

                    return (
                      <div key={field.key}>
                        <label className="mb-2 block text-sm font-semibold text-zinc-300">
                          {field.label}
                        </label>
                        <input type={field.type || "text"} {...common} />
                      </div>
                    );
                  })}

                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-semibold text-zinc-300">
                      Hours
                    </label>
                    <textarea
                      rows={3}
                      name="hours"
                      value={form.hours || ""}
                      onChange={onChange}
                      placeholder="Mon–Thu 11am–9pm · Fri–Sat 11am–10pm · Sun 12pm–8pm"
                      data-testid="mockup-input-hours"
                      className="w-full resize-none rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-white placeholder-zinc-500 transition-all focus:border-cyan-400 focus:outline-none"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="w-full rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.04] p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
              Edit Another Section
            </div>
            <p className="mt-2 text-sm text-zinc-400">
              Jump back to any part of the Restaurant Page editor without scrolling.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {[
                ["1. Promotion / Event", "admin-section-type"],
                ["2. Page Photo", "admin-section-photo"],
                ["3. Page Content", "admin-section-content"],
                ["4. Customer Action", "admin-section-action"],
                ["5. Restaurant Info", "admin-section-info"],
              ].map(([label, sectionId]) => (
                <button
                  key={sectionId}
                  type="button"
                  onClick={() => jumpToAdminSection(sectionId)}
                  className="min-h-[46px] rounded-xl border border-white/10 bg-black/20 px-3 py-2.5 text-sm font-semibold text-zinc-200 transition-colors hover:border-cyan-400/35 hover:bg-cyan-400/10"
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="mt-3">
              <button
                type="button"
                onClick={jumpToAdminQuickEdit}
                className="min-h-[46px] w-full rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-3 py-2.5 text-sm font-semibold text-cyan-200"
              >
                ↑ Back to Quick Edit
              </button>
            </div>
          </div>

          <div className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
              Admin Notes · Internal Only
            </div>
            <p className="mt-2 text-xs leading-5 text-zinc-500">
              Add a common note quickly or type anything you need below. These
              notes are never shown on the Restaurant Page or Client Dashboard.
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
              <select
                value={quickAdminNote}
                onChange={(e) => setQuickAdminNote(e.target.value)}
                className="min-h-[48px] w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 text-white focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                data-testid="mockup-admin-quick-note"
              >
                <option value="">Choose a quick note...</option>
                {ADMIN_QUICK_NOTE_OPTIONS.map((note) => (
                  <option key={note} value={note}>
                    {note}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={addQuickAdminNote}
                className="min-h-[48px] rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-5 py-3 text-sm font-bold text-cyan-200 transition-colors hover:bg-cyan-400/15"
                data-testid="mockup-add-quick-note"
              >
                Add Quick Note
              </button>
            </div>

            <div className="mt-4">
              <label className="mb-2 block text-xs font-semibold text-zinc-300">
                Custom Notes
              </label>
              <textarea
                rows={5}
                name="notes"
                value={form.notes || ""}
                onChange={onChange}
                placeholder="Type anything you want to remember about this restaurant..."
                data-testid="mockup-input-notes"
                className="w-full resize-y rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 text-white placeholder-zinc-500 transition-all focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
              />
            </div>
          </div>
        </div>

      </div>

      <div className="mt-8">
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
          <div className="h-1 w-full bg-cyan-400" />
          <div className="p-5">
            <div className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
              Final Step · Save &amp; Review
            </div>

            <h3 className="mt-2 font-display text-xl font-semibold text-white">
              {isEdit
                ? "Save & Review Your Restaurant Page"
                : "Create & Review Your Restaurant Page"}
            </h3>

            <p className="mt-1 text-sm text-zinc-400">
              The preview updates immediately.{" "}
              {isEdit
                ? "Click Save Restaurant Page Changes when everything looks right."
                : "Click Create Restaurant Page when everything looks right."}
              {isEdit
                ? " Outreach is saved separately when you use its controls."
                : ""}
            </p>

            {hasUnsavedChanges ? (
              <div className="mt-3 text-xs font-semibold text-amber-300">
                Restaurant Page has unsaved changes.
              </div>
            ) : isEdit ? (
              <div className="mt-3 text-xs text-emerald-300">
                Restaurant Page changes are saved.
              </div>
            ) : null}

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-cyan-400 px-5 py-3 font-semibold text-black transition-colors hover:bg-cyan-300 disabled:opacity-50"
                data-testid="mockup-form-submit"
              >
                {restaurantSaveLabel}
              </button>

              {isEdit && slug ? (
                <a
                  href={restaurantPageUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border border-cyan-400/40 bg-transparent px-5 py-3 font-semibold text-cyan-200 transition-colors hover:bg-cyan-400/10"
                >
                  <span aria-hidden>👁</span>
                  <span>{restaurantPageButtonLabel}</span>
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 flex">
        <Link
          to={restaurantPagesPath}
          className="text-sm text-zinc-400 transition-colors hover:text-white"
          data-testid="mockup-form-cancel"
        >
          ← Back to Restaurant Pages
        </Link>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#09090B]/95 p-2 backdrop-blur lg:hidden">
        <div className="mx-auto grid max-w-xl grid-cols-3 gap-2">
          <button
            type="button"
            onClick={jumpToAdminQuickEdit}
            className="min-h-[46px] rounded-xl border border-white/10 bg-white/5 px-2 py-2 text-xs font-bold text-zinc-200"
          >
            Sections
          </button>

          <button
            type="button"
            onClick={jumpToAdminPreview}
            className="min-h-[46px] rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-2 py-2 text-xs font-bold text-cyan-200"
          >
            Preview
          </button>

          <button
            type="submit"
            disabled={saving}
            className="min-h-[46px] rounded-xl bg-cyan-400 px-2 py-2 text-xs font-bold text-black disabled:opacity-50"
          >
            {isEdit ? (saving ? "Saving..." : "Save") : saving ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
          {mobilePreviewOpen ? (
        <div className="fixed inset-0 z-50 bg-black/80 p-4 lg:hidden">
          <div className="mx-auto flex h-full max-w-md flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#09090B]">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                  What Customers See
                </p>
                <p className="mt-1 text-sm text-zinc-400">
                  Your changes appear here instantly.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setMobilePreviewOpen(false)}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-white"
              >
                Close
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <AdminLiveUpdatePreview
                form={form}
                screen={adminPreviewScreen}
                onScreenChange={setAdminPreviewScreen}
                eventDestinationType={eventDestinationType}
              />
            </div>
          </div>
        </div>
      ) : null}

</form>
  );
}

function AdminLiveUpdatePreview({ form, screen = "primary", onScreenChange, eventDestinationType = "none" }) {
  const isEvent = (form.campaign_type || "offer") === "event";
  const isFeaturedItem = form.campaign_type === "featured_item";
  const utilityButtons = adminUtilityButtons(form);
  const themeColor = adminThemeColor(form);
  const offerText = form.custom_offer || form.offer || "Your offer appears here.";
  const claimText =
    form.custom_cta_text || form.cta_text || "Claim My Offer";
  const eventActionText = resolvedEventActionText(form);
  const afterClaimText = resolvedAfterClaimText(form);

  return (
    <div>
      <div className="mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00E5FF]">
          Live Update
        </div>
        <h3 className="mt-1 font-display text-lg font-semibold text-white">
          Customer Page Preview
        </h3>
        <p className="mt-1 text-xs leading-5 text-zinc-400">
          Updates instantly while you edit. Save changes to publish them.
        </p>
      </div>

      {isEvent ? (
        <div className="mb-3 grid grid-cols-2 gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-1.5">
          {[
            ["primary", "Event Page"],
            ["secondary", "After Button Tap"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => onScreenChange?.(value)}
              className={`rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                screen === value
                  ? "bg-[#00E5FF] text-black"
                  : "text-zinc-400 hover:bg-white/5 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-[28px] border border-white/10 bg-[#09090B] shadow-2xl">
        {form.hero_image_url ? (
          <div className="relative h-56 overflow-hidden bg-black">
            <img
              src={form.hero_image_url}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/60 to-transparent px-5 pb-5 pt-16">
              <div className="text-xl font-bold text-white">
                {form.restaurant_name || "Restaurant Name"}
              </div>
              <div className="mt-1 text-xs text-zinc-300">
                {form.instagram_handle
                  ? `@${String(form.instagram_handle).replace(/^@/, "")}`
                  : "@instagram"}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-48 items-center justify-center bg-white/5 px-6 text-center text-sm text-zinc-500">
            Restaurant photo will appear here.
          </div>
        )}

        <div className="p-4">
          <div className="text-center text-xs text-zinc-400">
            {[form.city, form.cuisine].filter(Boolean).join(" · ") ||
              "City · Cuisine"}
          </div>

          {isEvent ? (
            screen === "primary" ? (
              <div className="mt-5">
                <div
                  className="rounded-2xl border p-4"
                  style={{ borderColor: `${themeColor}66` }}
                >
                  <div
                    className="text-[10px] font-bold uppercase tracking-[0.18em]"
                    style={{ color: themeColor }}
                  >
                    Event Page
                  </div>
                  <div className="mt-2 text-lg font-bold text-white">
                    {form.event_name || "Your Event Name"}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-zinc-300">
                    <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5">
                      {adminEventDateLabel(form.event_date)}
                    </span>
                    <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5">
                      {form.event_time || "Event time"}
                    </span>
                  </div>

                  <p className="mt-3 text-sm leading-5 text-zinc-300">
                    {form.event_description ||
                      "Your event description appears here."}
                  </p>
                </div>

                <div
                  className="mt-4 rounded-xl px-4 py-3 text-center text-sm font-bold text-black"
                  style={{ backgroundColor: themeColor }}
                >
                  {eventActionText}
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-2xl border border-fuchsia-400/25 bg-zinc-950 p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-300">
                  After Button Tap
                </div>

                <div
                  className="mt-4 rounded-xl border px-4 py-3 text-center text-sm font-semibold"
                  style={{
                    borderColor: `${themeColor}80`,
                    backgroundColor: `${themeColor}22`,
                    color: themeColor,
                  }}
                >
                  {eventActionText}
                </div>

                <p className="mt-2 text-center text-[11px] leading-4 text-zinc-500">
                  {eventDestinationLink(form, eventDestinationType)
                    ? "This button opens the destination selected in Step 4."
                    : "No destination link is set yet."}
                </p>

              </div>
            )

          ) : isFeaturedItem ? (
            <div className="mt-5">
              <div
                className="rounded-2xl border p-4"
                style={{ borderColor: `${themeColor}66` }}
              >
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.18em]"
                  style={{ color: themeColor }}
                >
                  Featured Item
                </div>
                <div className="mt-2 text-lg font-bold text-white">
                  {form.featured_item_name || "Your Featured Item"}
                </div>
                <p className="mt-2 text-sm leading-5 text-zinc-300">
                  {form.featured_item_description ||
                    "Your featured item description appears here."}
                </p>
              </div>

              <div
                className="mt-4 rounded-xl px-4 py-3 text-center text-sm font-bold text-black"
                style={{ backgroundColor: themeColor }}
              >
                {form.featured_action_text || "View Menu"}
              </div>
              <p className="mt-2 text-center text-xs text-zinc-500">
                {form.featured_action_link || form.menu_url
                  ? "Button destination is configured."
                  : "Add a destination link for the customer button."}
              </p>
            </div>
          ) : (

            <div className="mt-5">
              <div
                className="rounded-2xl border p-4"
                style={{ borderColor: `${themeColor}66` }}
              >
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.18em]"
                  style={{ color: themeColor }}
                >
                  Current Offer
                </div>
                <div className="mt-2 text-lg font-bold text-white">
                  {offerText}
                </div>
                <p className="mt-2 text-sm leading-5 text-zinc-300">
                  {form.description ||
                    "Your short description appears here."}
                </p>
              </div>

              <div
                className="mt-4 rounded-xl px-4 py-3 text-center text-sm font-bold text-black"
                style={{ backgroundColor: themeColor }}
              >
                {claimText}
              </div>

              <div className="mt-5 rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.04] p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                  After Claim Preview
                </div>
                <div className="mt-2 text-sm font-semibold text-white">
                  {afterClaimText}
                </div>
                <div className="mt-1 text-[11px] leading-4 text-zinc-500">
                  This is the main action shown after the customer receives the claim code.
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-3 gap-2">
            {["Website", "Call", "Directions"].map((label) => {
              const ready = utilityButtons.some(
                (button) => button.label === label
              );

              return (
                <div
                  key={label}
                  className={`rounded-xl border px-1 py-2.5 text-center text-[11px] font-semibold ${
                    ready
                      ? "border-white/10 bg-white/5 text-white"
                      : "border-white/5 bg-black/20 text-zinc-600"
                  }`}
                >
                  {label}
                </div>
              );
            })}
          </div>

          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-xs leading-5 text-zinc-400">
            <div>{form.address || "Restaurant address"}</div>
            <div>{form.phone || "Restaurant phone"}</div>
          </div>
        </div>
      </div>

      <div className="mt-3 rounded-xl border border-amber-400/20 bg-amber-400/5 px-3 py-3 text-xs leading-5 text-zinc-400">
        {isEvent ? (
          <>
            Use <span className="font-semibold text-white">Event Page</span> and <span className="font-semibold text-white">After Button Tap</span> to review both parts of the event experience. After saving, use the page button below: it opens the live /r page when one exists, otherwise it opens the sales demo.
          </>
        ) : (
          <>
            This preview is for editing. After saving, use the page button below: it opens the live /r page when one exists, otherwise it opens the sales demo.
          </>
        )}
      </div>
    </div>
  );
}

